import type { ServicePageConfig } from "../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../package-pricing";

export const tömningarPages: ServicePageConfig[] = [
  {
    // Tömma källare — config för /tomma-kallare
    // Vinkel: Komplett källartömning — allt från gammalt bohag till grovsopor.
    // Unik vinkel vs bortforsling: fokus på hela utrymmet, fukt, tillgänglighet, BRF-regler.
    slug: "tomma-kallare",
    serviceCategory: "tömningar",

    meta: {
      title: "Tömma källare – Snabb & Prisvärd Källartömning | Ungdomskraft",
      description:
        "Boka källartömning i Stockholm och Göteborg. Vi tömmer, bär ut och kör bort allt från din källare. Fast pris, försäkrat – ofta samma dag. Boka idag!",
      h1: "Tömma källare",
    },

    hero: {
      heading: "Hjälp att tömma källare",
      intro:
        "Har källaren blivit en samlingsplats för saker du aldrig använder? Vi tömmer hela källaren åt dig – snabbt, smidigt och till fast pris. Våra ungdomar bär ut allt, oavsett hur trångt eller rörigt det är, och kör det till återvinning eller välgörenhet. Du behöver bara visa vad som ska bort.",
      heroImage: "/hero-bortforsling.webp",
    },

    featureCards: {
      headingText: "Vi sköter din källartömning",
      sinceText: "Källartömning sedan 2014",
      bookYourText: "Boka din källartömning",
      bookDescText:
        "Ange din adress, beskriv vad som finns i källaren, och boka en tid som passar dig. Du ser priset direkt.",
      vehicleTitleText: "Anpassat fordon för din källartömning",
      vehicleDescText:
        "Vi har flera fordon i olika storlekar. Vi väljer det fordon som passar ditt uppdrag bäst, så att källartömningen blir så effektiv och kostnadseffektiv som möjligt.",
    },

    itemsSection: {
      heading: "Vanligt i källare vi tömmer",
      items: [
        {
          name: "Möbler",
          description: "Soffor, bord, stolar och bokhyllor som samlat damm",
          image: "/sofa.webp",
        },
        {
          name: "Kartonger & lådor",
          description: "Flyttkartonger, plastbackar och förvaringslådor",
          image: "/box.webp",
        },
        {
          name: "Vitvaror",
          description: "Gamla kylskåp, frysar, tvättmaskiner och torktumlare",
          image: "/fridge.webp",
        },
        {
          name: "Elektronik",
          description: "TV-apparater, datorer, skärmar och kablar",
          image: "/electronics.webp",
        },
        {
          name: "Grovsopor",
          description: "Madrasser, mattor, lampor och övrigt skrymmande",
          image: "/recycling-bin.webp",
        },
        {
          name: "Verktyg & byggmaterial",
          description: "Överblivet virke, färgburkar, verktyg och renoveringsrester",
          image: "/toolbox.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Professionell källartömning",
      mainText:
        "Källaren är ofta det utrymme i hemmet som fylls snabbast och töms sist. Flyttkartonger, gamla möbler, trasig elektronik och saker du inte riktigt vet vart du ska göra av – allt hamnar där nere. Med tiden blir källaren så full att du knappt kan ta dig in, och tanken på att tömma den känns överväldigande.\n\nMed Ungdomskraft slipper du göra det själv. Vi skickar unga, energiska medarbetare som bär ut allt ur källaren, oavsett hur trångt, mörkt eller svårtillgängligt det är. Vi sorterar på plats: saker i bra skick skänks vidare till organisationer som Myrorna och Stadsmissionen, resten körs till godkänd återvinningsstation. Du får tillbaka ett tomt, användbart utrymme utan att behöva lyfta ett finger.",
      preparationHeading: "Förbered inför din källartömning",
      preparationText:
        "Du behöver inte förbereda något särskilt – våra ungdomar hanterar allt. Men om du vill snabba på processen kan du se till att det finns en fri väg från källardörren till gatan. Kontrollera också att nycklar eller portkoder finns tillgängliga om vi behöver passera låsta dörrar. Bor du i en BRF? Stäm av med styrelsen om det finns regler kring tider och transportvägar i huset.",
      additionalSections: [
        {
          heading: "Varför boka källartömning med Ungdomskraft?",
          listItems: [
            {
              label: "Enkel bokning utan krångel –",
              text: "Boka din källartömning på några minuter. Fyll i adress, välj datum och få pris direkt – slipp väntan på offert. Behöver du hjälp redan idag? Vi kan vara på plats redan inom 60 minuter. Du kan även ringa oss på 08-517 082 81.",
            },
            {
              label: "Vi tömmer hela källaren åt dig –",
              text: "Du behöver inte sortera, bära eller ens vara på plats. Våra ungdomar tar sig in i källaren, bär ut allt och sorterar det som kan återvinnas eller skänkas. Trånga trappor, tunga saker och låga tak – vi löser det.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar allt vi tömmer ur källaren. Saker i gott skick skänks vidare. Elektronik lämnas till godkänd e-avfallsmottagning. Resten körs till återvinningsstation – inget hamnar där det inte ska. Du kan även ha rätt till RUT-avdrag när saker transporteras till återanvändning.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg-Hansa. Din fastighet, dina ägodelar och våra ungdomar är skyddade under hela processen – från lyft till transport.",
            },
          ],
        },
        {
          heading: "Vad brukar finnas i en källare?",
          content:
            "Källare fylls med lite av varje genom åren. Vi har tömt hundratals källare och vet precis vad vi kan förvänta oss:",
          listItems: [
            {
              label: "Möbler –",
              text: "gamla soffor, fåtöljer, bord, stolar, bokhyllor och garderober som inte längre används",
            },
            {
              label: "Kartonger & lådor –",
              text: "flyttkartonger, plastbackar och förvaringslådor med blandat innehåll",
            },
            {
              label: "Vitvaror –",
              text: "uttjänta kylskåp, frysar, tvättmaskiner och torktumlare",
            },
            {
              label: "Elektronik –",
              text: "gamla TV-apparater, datorer, skärmar, kablar och annat elavfall",
            },
            {
              label: "Sport- och fritidsutrustning –",
              text: "cyklar, skidutrustning, träningsredskap och campingprylar",
            },
            {
              label: "Verktyg & byggmaterial –",
              text: "överblivet virke, färgburkar, rör och renoveringsrester",
            },
            {
              label: "Trädgårdstillbehör –",
              text: "krukor, jordsäckar, grillar och trädgårdsmöbler som förvarats över vintern",
            },
          ],
          afterContent:
            "Vi tar även emot blandade lass där allt ska bort på en gång. Osäker på om vi kan hantera just ditt källarinnehåll? Hör av dig så berättar vi mer.",
        },
        {
          heading: "Så fungerar det – källartömning i tre enkla steg",
          listItems: [
            {
              label: "1. Beskriv din källare –",
              text: "Fyll i vårt bokningsformulär med adress, ungefärlig mängd och önskat datum. Du får ett pris direkt – inga dolda kostnader.",
            },
            {
              label: "2. Vi tömmer källaren –",
              text: "Våra ungdomar kommer till dig, tar sig ner i källaren och bär ut allt. De hanterar trånga trappor, låga tak och tunga föremål – du behöver inte lyfta ett finger.",
            },
            {
              label: "3. Ansvarsfull hantering –",
              text: "Vi kör allt till rätt ställe. Saker som kan återanvändas skänks vidare, resten sorteras och lämnas på godkänd återvinningsstation.",
            },
          ],
        },
        {
          heading: "Erfarna källartömmare med lokalkännedom",
          content:
            "Våra ungdomar har gedigen erfarenhet av att tömma källare i alla möjliga fastighetstyper – från gamla flerfamiljshus i innerstaden med branta, smala trappor till moderna villor med stora källarplan. De vet hur man manövrerar tunga möbler genom trånga passager och hur man lastar effektivt för att minimera antalet turer.\n\nVi utför dagligen källartömningar av allt från enstaka förråd till kompletta källarplan i BRF:er. Den erfarenheten gör att vi kan ge dig ett exakt pris redan vid bokning och genomföra uppdraget smidigt och utan fördröjningar.",
        },
        {
          heading: "Boka källartömning idag",
          content:
            "Vill du äntligen få ordning på källaren? Med Ungdomskraft är det enkelt. Vi tömmer, bär och kör bort allt – du slipper tänka på sortering, transport eller återvinning. Fyll i formuläret och få pris direkt. Inga offertförfrågningar, bara smidig källartömning som gör skillnad.\n\nSjälvklart kan du även ringa oss på 08-517 082 81 för att boka din källartömning.",
          cta: { text: "Boka källartömning", href: "#priser" },
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar det att tömma en källare?",
        answer:
          "Priset beror på mängden saker, deras vikt och hur lång tid uppdraget tar. Du får ett pris direkt i vår bokning baserat på vad du anger – inga dolda avgifter. En mindre källare med ett par möbler och kartonger hamnar ofta på 995–1 495 kr. Större tömningar med tungt innehåll kan kosta 2 495–4 995 kr eller mer. Avfallsavgifter på återvinningsstationen tillkommer separat och ligger oftast runt 400 kr.",
      },
      {
        question: "Hur lång tid tar det att tömma en källare?",
        answer:
          "Det varierar beroende på mängd och tillgänglighet. En mindre källare med ett fåtal möbler och kartonger tar vanligtvis 30–60 minuter. En stor, fullpackad källare kan ta en halv till en hel dag. Våra ungdomar jobbar effektivt och är vana vid trånga utrymmen – vi ser till att det går så snabbt som möjligt.",
      },
      {
        question: "Behöver jag vara hemma vid källartömningen?",
        answer:
          "Nej, du behöver inte vara hemma om du inte vill. Ge oss tillgång till källaren via nyckel, portkod eller genom att lämna den olåst, så sköter vi resten. Många kunder lämnar instruktioner via appen och låter våra ungdomar jobba självständigt.",
      },
      {
        question: "Kan ni tömma källarförråd i flerfamiljshus?",
        answer:
          "Absolut. Vi tömmer regelbundet källarförråd i BRF:er, hyreshus och flerfamiljshus. Våra ungdomar är vana vid smala källarkorridorer, låga tak och tunga lyft i trappor. Vi ser till att gemensamma ytor hålls rena under arbetet.",
      },
      {
        question: "Vad händer med sakerna efter källartömningen?",
        answer:
          "Vi sorterar allt vi tömmer. Saker i gott skick skänks vidare till organisationer som Myrorna eller Stadsmissionen. Elektronik lämnas till godkänd e-avfallsmottagning. Resten – trä, metall, textil och blandat avfall – sorteras och körs till godkänd återvinningsstation. Vårt mål är alltid att så lite som möjligt hamnar på deponi.",
      },
      {
        question: "Tar ni hand om farligt avfall i källaren?",
        answer:
          "Vi hanterar inte farligt avfall som kemikalier, lösningsmedel, färgrester eller asbest. Däremot kan vi ta med färgburkar och mindre mängder byggavfall till återvinningsstationen om de är väl förpackade. Är du osäker? Hör av dig så hjälper vi dig avgöra vad vi kan ta med.",
      },
      {
        question: "Hur snabbt kan ni komma och tömma min källare?",
        answer:
          "Vi kan ofta vara på plats samma dag. Du kan boka med så kort varsel som 30 minuter eller planera upp till 30 dagar i förväg. Välj den tid som passar dig bäst – du ser tillgängliga tider direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på källartömning?",
        answer:
          "Om sakerna från din källare transporteras till en secondhand-butik, välgörenhetsorganisation eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Vanlig källartömning där allt körs direkt till återvinningsstation ger inte rätt till RUT-avdrag. Avdraget hanteras direkt på fakturan, så du behöver aldrig ligga ute med pengarna.",
      },
      {
        question: "Ingår avfallsavgifter i priset för källartömningen?",
        answer:
          "Nej, avfalls- och deponiavgifter ingår inte i priset utan debiteras separat. Avgiften varierar beroende på återvinningsstation samt vikten och typen av avfall, men ligger oftast runt 400 kr. Du ser alltid den totala kostnaden innan vi påbörjar uppdraget.",
      },
      {
        question: "Kan ni tömma en källare inför en renovering?",
        answer:
          "Ja, vi hjälper ofta till med att tömma källare inför renoveringar, ombyggnationer eller försäljning. Vi bär ut allt – från möbler och kartonger till byggavfall och skrymmande föremål – så att hantverkarna kan komma igång direkt.",
      },
      {
        question: "Kan ni tömma källaren i ett dödsbo?",
        answer:
          "Ja, vi har gedigen erfarenhet av att tömma källare i samband med dödsbon. Våra ungdomar hanterar allt varsamt och sorterar noggrant. Saker i bra skick skänks vidare, resten lämnas på godkänd återvinningsstation. Du behöver inte vara på plats om du inte vill.",
      },
      {
        question: "Kan ni tömma en källare med fukt- eller mögelskador?",
        answer:
          "Ja, vi tömmer regelbundet källare med fukt- och mögelproblematik. Våra ungdomar använder skyddsutrustning vid behov och hanterar materialet varsamt så att sporer inte sprids i resten av fastigheten. Vi tar dock inte hand om själva saneringen – det bör en specialiserad firma göra efter att källaren är tömd.",
      },
      {
        question: "Erbjuder ni källartömning för hela BRF:er?",
        answer:
          "Absolut. Vi hjälper BRF:er med att tömma gemensamma källarutrymmen, cykelrum och förrådskorridorer. Vi samordnar med styrelsen kring tider och transportvägar och kan tömma flera källarförråd i ett och samma uppdrag, vilket ofta ger ett bättre totalpris.",
      },
      {
        question: "Vilka områden erbjuder ni källartömning i?",
        answer:
          "Vi erbjuder källartömning i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Tömma källare i 3 enkla steg",
      step2Text:
        "Våra ungdomar tar sig ner i källaren, bär ut allt och lastar på bilen. Trånga trappor, låga tak och tunga saker – vi löser det. Du behöver inte lyfta ett finger.",
      ctaHeading: "Boka källartömning idag",
      ctaText:
        "Fyll i adressen, beskriv vad som finns i källaren, och välj en tid som passar. Du ser priset direkt — inga dolda kostnader.",
    },

    en: {
      slug: "basement-clearance",

      featureCards: {
        sinceText: "Basement clearance since 2014",
        bookYourText: "Book your basement clearance",
        bookDescText:
          "Enter your address, describe what's in the basement, and pick a time that suits you. You see the price instantly.",
        vehicleTitleText: "The right vehicle for your basement clearance",
        vehicleDescText:
          "We have multiple vehicles in different sizes. We pick the one that best fits your job, making the basement clearance as efficient and cost-effective as possible.",
      },

      meta: {
        title: "Basement Clearance – Fast & Affordable | Ungdomskraft",
        description:
          "Book basement clearance quickly and easily. We clear out, carry and haul away everything from your basement. Fixed price, simple booking – often same day.",
        h1: "Basement clearance",
      },

      hero: {
        heading: "Help clearing your basement",
        intro:
          "Has your basement become a dumping ground for things you never use? We clear the entire basement for you – fast, hassle-free and at a fixed price. Our young workers carry everything out, no matter how cramped or cluttered, and take it to recycling or charity. You just show us what needs to go.",
      },

      itemsSection: {
        heading: "Common items in basements we clear",
        items: [
          {
            name: "Furniture",
            description: "Sofas, tables, chairs and bookshelves collecting dust",
            image: "/sofa.webp",
          },
          {
            name: "Boxes & crates",
            description: "Moving boxes, plastic bins and storage containers",
            image: "/box.webp",
          },
          {
            name: "Appliances",
            description: "Old fridges, freezers, washing machines and dryers",
            image: "/fridge.webp",
          },
          {
            name: "Electronics",
            description: "TVs, computers, monitors and cables",
            image: "/electronics.webp",
          },
          {
            name: "Bulky waste",
            description: "Mattresses, rugs, lamps and other oversized items",
            image: "/recycling-bin.webp",
          },
          {
            name: "Tools & building materials",
            description: "Leftover timber, paint cans, tools and renovation debris",
            image: "/toolbox.webp",
          },
        ],
      },

      seoContent: {
        mainHeading: "Professional basement clearance",
        mainText:
          "The basement is often the space in a home that fills up fastest and gets emptied last. Moving boxes, old furniture, broken electronics and things you're not sure what to do with – it all ends up down there. Over time the basement gets so full you can barely get in, and the thought of clearing it feels overwhelming.\n\nWith Ungdomskraft you don't have to do it yourself. We send young, energetic workers who carry everything out of the basement, no matter how cramped, dark or hard to reach it is. We sort on site: items in good condition are donated to charity organisations, the rest is taken to an approved recycling station. You get back an empty, usable space without lifting a finger.",
        preparationHeading: "Prepare for your basement clearance",
        preparationText:
          "You don't need to prepare anything special – our workers handle it all. But if you want to speed things up, make sure there's a clear path from the basement door to the street. Also check that keys or entry codes are available if we need to pass through locked doors. Live in a housing association? Check with the board about any rules regarding times and transport routes in the building.",
        additionalSections: [
          {
            heading: "Why book basement clearance with Ungdomskraft?",
            listItems: [
              {
                label: "Simple booking, no hassle –",
                text: "Book your basement clearance in minutes. Enter your address, pick a date and get an instant price – no waiting for quotes. Need help today? We can be there within 60 minutes. You can also call us at +46 8-517 082 81.",
              },
              {
                label: "We clear the entire basement for you –",
                text: "You don't need to sort, carry or even be present. Our workers get into the basement, carry everything out and sort what can be recycled or donated. Narrow stairs, heavy items and low ceilings – we handle it.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort everything we clear from the basement. Items in good condition are donated. Electronics go to approved e-waste collection. The rest is taken to a recycling station – nothing ends up where it shouldn't. You may also be eligible for the Swedish RUT tax deduction when items are transported for reuse.",
              },
              {
                label: "Insured and secure –",
                text: "Every job is insured through Trygg-Hansa. Your property, your belongings and our workers are protected throughout the entire process – from lifting to transport.",
              },
            ],
          },
          {
            heading: "What's usually found in a basement?",
            content:
              "Basements fill up with a bit of everything over the years. We've cleared hundreds of basements and know exactly what to expect:",
            listItems: [
              {
                label: "Furniture –",
                text: "old sofas, armchairs, tables, chairs, bookshelves and wardrobes that are no longer used",
              },
              {
                label: "Boxes & crates –",
                text: "moving boxes, plastic bins and storage containers with mixed contents",
              },
              {
                label: "Appliances –",
                text: "worn-out fridges, freezers, washing machines and dryers",
              },
              {
                label: "Electronics –",
                text: "old TVs, computers, monitors, cables and other e-waste",
              },
              {
                label: "Sports & leisure equipment –",
                text: "bicycles, ski gear, exercise equipment and camping gear",
              },
              {
                label: "Tools & building materials –",
                text: "leftover timber, paint cans, pipes and renovation debris",
              },
              {
                label: "Garden accessories –",
                text: "pots, bags of soil, barbecues and garden furniture stored over winter",
              },
            ],
            afterContent:
              "We also take mixed loads where everything needs to go at once. Not sure if we can handle your basement contents? Get in touch and we'll tell you more.",
          },
          {
            heading: "How it works – basement clearance in three easy steps",
            listItems: [
              {
                label: "1. Describe your basement –",
                text: "Fill in our booking form with your address, approximate amount and preferred date. You get an instant price – no hidden costs.",
              },
              {
                label: "2. We clear the basement –",
                text: "Our workers come to you, head down into the basement and carry everything out. They handle narrow stairs, low ceilings and heavy items – you don't have to lift a finger.",
              },
              {
                label: "3. Responsible handling –",
                text: "We take everything to the right place. Items that can be reused are donated, the rest is sorted and taken to an approved recycling station.",
              },
            ],
          },
          {
            heading: "Experienced basement clearers with local knowledge",
            content:
              "Our workers have extensive experience clearing basements in all types of properties – from old apartment buildings in the city centre with steep, narrow stairs to modern houses with large basement levels. They know how to manoeuvre heavy furniture through tight passages and how to load efficiently to minimise the number of trips.\n\nWe carry out basement clearances daily, from single storage units to entire basement floors in housing associations. That experience means we can give you an exact price at booking and complete the job smoothly and without delays.",
          },
          {
            heading: "Book basement clearance today",
            content:
              "Want to finally get your basement sorted? With Ungdomskraft it's easy. We clear, carry and haul everything away – you don't have to worry about sorting, transport or recycling. Fill in the form and get an instant price. No quote requests, just smooth basement clearance that makes a difference.\n\nYou can also call us at +46 8-517 082 81 to book your basement clearance.",
            cta: { text: "Book basement clearance", href: "#priser" },
          },
        ],
      },

      faq: [
        {
          question: "How much does it cost to clear a basement?",
          answer:
            "The price depends on the amount of items, their weight and how long the job takes. You get an instant price in our booking based on what you enter – no hidden fees. A smaller basement with a couple of furniture items and boxes typically costs SEK 995–1,495. Larger clearances with heavy contents can cost SEK 2,495–4,995 or more. Waste disposal fees at the recycling station are charged separately and usually run around SEK 400.",
        },
        {
          question: "How long does it take to clear a basement?",
          answer:
            "It varies depending on the amount and accessibility. A smaller basement with a few pieces of furniture and boxes usually takes 30–60 minutes. A large, fully packed basement can take half a day to a full day. Our workers are efficient and used to tight spaces – we make sure it goes as quickly as possible.",
        },
        {
          question: "Do I need to be home during the basement clearance?",
          answer:
            "No, you don't need to be home if you'd rather not. Give us access to the basement via a key, entry code or by leaving it unlocked, and we take care of the rest. Many customers leave instructions through the app and let our workers handle it independently.",
        },
        {
          question: "Can you clear basement storage units in apartment buildings?",
          answer:
            "Absolutely. We regularly clear basement storage units in housing associations and apartment buildings. Our workers are used to narrow basement corridors, low ceilings and heavy lifting on stairs. We make sure shared areas are kept clean during the work.",
        },
        {
          question: "What happens to the items after the basement clearance?",
          answer:
            "We sort everything we clear out. Items in good condition are donated to charity organisations. Electronics go to approved e-waste collection. The rest – wood, metal, textiles and mixed waste – is sorted and taken to an approved recycling station. Our goal is always to send as little as possible to landfill.",
        },
        {
          question: "Do you handle hazardous waste from basements?",
          answer:
            "We don't handle hazardous waste such as chemicals, solvents, paint residue or asbestos. However, we can take paint cans and smaller amounts of construction waste to the recycling station if they are properly packaged. Not sure? Get in touch and we'll help you figure out what we can take.",
        },
        {
          question: "How quickly can you come and clear my basement?",
          answer:
            "We can often be on site the same day. You can book with as little as 30 minutes' notice or plan up to 30 days ahead. Choose the time that suits you best – you see available times directly when booking.",
        },
        {
          question: "Are waste disposal fees included in the price?",
          answer:
            "No, waste and landfill fees are not included in the price and are charged separately. The fee varies depending on the recycling station and the weight and type of waste, but usually runs around SEK 400. You always see the total cost before we start the job.",
        },
        {
          question: "Can you clear a basement before a renovation?",
          answer:
            "Yes, we often help clear basements ahead of renovations, remodelling or property sales. We carry everything out – from furniture and boxes to construction waste and bulky items – so the contractors can get started right away.",
        },
        {
          question: "Can you clear a basement as part of an estate clearance?",
          answer:
            "Yes, we have extensive experience clearing basements in connection with estate clearances. Our workers handle everything with care and sort thoroughly. Items in good condition are donated, the rest is taken to an approved recycling station. You don't need to be present if you'd rather not.",
        },
        {
          question: "Can you clear a basement with damp or mould issues?",
          answer:
            "Yes, we regularly clear basements with damp and mould problems. Our workers use protective equipment when needed and handle the material carefully to prevent spores from spreading in the building. We don't carry out the actual remediation – that should be done by a specialist firm once the basement is cleared.",
        },
        {
          question: "Do you offer basement clearance for entire housing associations?",
          answer:
            "Absolutely. We help housing associations clear shared basement areas, bike rooms and storage corridors. We coordinate times and transport routes with the board and can clear multiple basement units in one go, which often gives a better overall price.",
        },
        {
          question: "Can I get a RUT tax deduction on basement clearance?",
          answer:
            "If items from your basement are transported to a charity shop or organisation for reuse, you're eligible for the Swedish RUT deduction on the labour cost. A standard clearance where everything goes straight to a recycling station does not qualify. The deduction is applied directly on the invoice, so you never have to pay the full amount upfront.",
        },
        {
          question: "Which areas do you offer basement clearance in?",
          answer:
            "We offer basement clearance throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,

      pricingRows: getPackagePricingRows("bortforsling", "en"),

      dynamicHeadings: {
        threeSteps: "Clear your basement in 3 easy steps",
        step2Text:
          "Our workers head down into the basement, carry everything out and load it onto the vehicle. Narrow stairs, low ceilings and heavy items – we handle it. You don't have to lift a finger.",
        ctaHeading: "Book basement clearance today",
        ctaText:
          "Enter your address, describe what's in the basement, and pick a time that works. You see the price instantly — no hidden costs.",
      },
    },
  },

  {
    // Tömma förråd — config för /tomma-forrad
    // Vinkel: Förrådstömning — hyrda förråd, BRF-förråd, uppsägningsdeadlines,
    // säsongsbyte, bortglömda saker. Unikt vs källare: extern plats, hyresavtal, tidsfrist.
    slug: "tomma-forrad",
    serviceCategory: "tömningar",

    meta: {
      title: "Tömma förråd – Snabb Förrådstömning till Fast Pris | Ungdomskraft",
      description:
        "Boka förrådstömning i Stockholm och Göteborg. Vi tömmer, bär ut och kör till återvinning. Perfekt vid uppsägning, flytt eller säsongsrensning. Fast pris!",
      h1: "Tömma förråd",
    },

    hero: {
      heading: "Hjälp att tömma förråd",
      intro:
        "Förrådet fullt och deadline för uppsägning närmar sig? Vi tömmer hela förrådet åt dig – oavsett om det ligger i källaren, på en förrådsanläggning eller i ett garage. Våra ungdomar bär ut, sorterar och kör bort allt. Du behöver bara ge oss tillgång.",
      heroImage: "/hero-bortforsling.webp",
    },

    featureCards: {
      headingText: "Vi sköter din förrådstömning",
      sinceText: "Förrådstömning sedan 2014",
      bookYourText: "Boka din förrådstömning",
      bookDescText:
        "Ange förådets adress, beskriv ungefär vad som finns där inne, och välj en tid som passar. Du ser priset direkt.",
      vehicleTitleText: "Anpassat fordon för din förrådstömning",
      vehicleDescText:
        "Litet förråd eller stort? Vi matchar fordonet efter ditt uppdrag – från skåpbil för ett par kartonger till lastbil med bakgavellyft för tunga möbler och vitvaror.",
    },

    itemsSection: {
      heading: "Saker vi hämtar från förråd",
      items: [
        {
          name: "Flyttkartonger",
          description: "Ouppackade kartonger och lådor från förra flytten",
          image: "/box.webp",
        },
        {
          name: "Säsongsförvaring",
          description: "Skid- och campingutrustning, vinterdäck och säsongskläder",
          image: "/fitness-equipment.webp",
        },
        {
          name: "Möbler",
          description: "Soffor, sängar, bord och stolar som väntat på nytt hem",
          image: "/sofa.webp",
        },
        {
          name: "Vitvaror",
          description: "Extra kylskåp, frysar och tvättmaskiner",
          image: "/fridge.webp",
        },
        {
          name: "Elektronik",
          description: "Gamla datorer, skärmar, högtalare och kablar",
          image: "/tv.webp",
        },
        {
          name: "Blandat",
          description: "Allt annat – verktyg, prydnader, barnprylar och husgeråd",
          image: "/appliances.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Smidig förrådstömning utan stress",
      mainText:
        "Det börjar ofta med en flyttlåda eller två som \"bara ska stå tillfälligt\". Några år senare är förrådet proppfullt med saker du knappt minns att du äger. Hyran tickar på varje månad, och tanken på att rensa ut allt på egen hand känns hopplös – speciellt om förrådet ligger på en annan adress.\n\nMed Ungdomskraft tar vi hand om hela processen. Vi åker till ditt förråd, bär ut allt, sorterar det som kan skänkas eller återvinnas och kör bort resten. Du sparar tid, slipper tunga lyft och kan säga upp hyresavtalet utan att stressa. Oavsett om förrådet är på Shurgard, 24Storage eller i föreningens källare gör vi jobbet åt dig.",
      preparationHeading: "Förbered inför din förrådstömning",
      preparationText:
        "Se till att vi kan ta oss in i förrådet – lämna nyckel, portkod eller hänglåskod. Om förrådet ligger på en extern anläggning, kontrollera att vi har rätt att hämta ut saker (vissa anläggningar kräver att du anmäler besökare i förväg). Vill du behålla något? Märk det tydligt eller peka ut det på plats, så lämnar vi det kvar.",
      additionalSections: [
        {
          heading: "Varför boka förrådstömning med Ungdomskraft?",
          listItems: [
            {
              label: "Slipp betala hyra i onödan –",
              text: "Varje månad du skjuter upp tömningen kostar pengar. Vi kan ofta vara på plats samma dag eller nästa dag, så att du hinner säga upp förrådet innan nästa debiteringsperiod. Boka online eller ring oss på 08-517 082 81.",
            },
            {
              label: "Vi hämtar på förrådsanläggningen –",
              text: "Du behöver inte ens transportera sakerna hem. Vi åker direkt till Shurgard, 24Storage, Pelican Self Storage, Green Storage eller vilken anläggning ditt förråd än ligger på – och tömmer det på plats.",
            },
            {
              label: "Sortering och återvinning ingår –",
              text: "Vi slänger inte allt rakt i containern. Saker i bra skick skänks vidare till secondhand, elektronik lämnas till godkänd e-avfallsmottagning, och resten sorteras på återvinningsstationen. Du kan ha rätt till RUT-avdrag om sakerna transporteras till återanvändning.",
            },
            {
              label: "Försäkrat via Trygg-Hansa –",
              text: "Alla uppdrag är helförsäkrade. Dina ägodelar och fastigheten är skyddade under hela tömningen – från lyft till transport.",
            },
          ],
        },
        {
          heading: "Vanliga situationer när folk tömmer förråd",
          content:
            "Vi hjälper privatpersoner och företag i alla möjliga situationer. Här är de vanligaste anledningarna till att våra kunder bokar förrådstömning:",
          listItems: [
            {
              label: "Flytt –",
              text: "du byter stad eller bostad och vill inte ta med allt som samlats i förrådet",
            },
            {
              label: "Uppsägning av hyresförråd –",
              text: "deadline närmar sig och förrådet måste vara tomt innan avtalet löper ut",
            },
            {
              label: "Säsongsrensning –",
              text: "du vill rensa ut allt du inte använt på över ett år för att frigöra plats",
            },
            {
              label: "Dödsbo –",
              text: "ett förråd tillhörande ett dödsbo som behöver tömmas i samband med bouppteckning",
            },
            {
              label: "Byte av förrådsstorlek –",
              text: "du vill byta till ett mindre och billigare förråd och behöver hjälp att rensa bort överskottet",
            },
            {
              label: "BRF-renovering –",
              text: "föreningen ska renovera källare eller förråd och alla boende måste tömma sina utrymmen",
            },
          ],
          afterContent:
            "Oavsett anledning gör vi det enkelt. Beskriv vad som finns i förrådet vid bokning, så skickar vi rätt fordon och antal ungdomar.",
        },
        {
          heading: "Så fungerar förrådstömning med Ungdomskraft",
          listItems: [
            {
              label: "1. Beskriv ditt förråd –",
              text: "Fyll i adressen till förrådets plats, ange ungefär hur mycket som ska bort och välj datum. Du får ett pris direkt – inga dolda kostnader.",
            },
            {
              label: "2. Vi tömmer förrådet –",
              text: "Våra ungdomar åker till förrådet, öppnar det, bär ut allt och lastar fordonet. Oavsett om det ligger i ett källarplan, en förrådsbox eller ett garage gör vi jobbet effektivt och varsamt.",
            },
            {
              label: "3. Sortering och återvinning –",
              text: "Allt som kan skänkas levererar vi till secondhand. Elektronik lämnas till e-avfall. Resten körs till godkänd återvinningsstation och sorteras där.",
            },
          ],
        },
        {
          heading: "Vi jobbar med alla typer av förråd",
          content:
            "Våra ungdomar har tömt hundratals förråd – från små BRF-förråd i trånga källarkorridorer till stora förradsboxar på moderna anläggningar. Vi vet vilka regler som gäller på de flesta anläggningar i Stockholm och Göteborg, och vi planerar uppdraget så att det går snabbt och smidigt.\n\nStorleken spelar ingen roll. Ett litet förråd på 2 kvadratmeter med bara kartonger tar kanske 20 minuter. Ett stort förråd fullpackat med möbler, cyklar och vitvaror kan ta ett par timmar. Vi ger dig ett exakt pris vid bokning oavsett storlek.",
        },
        {
          heading: "Boka förrådstömning idag",
          content:
            "Sluta betala hyra för ett förråd du inte behöver. Boka en förrådstömning med Ungdomskraft, så sköter vi resten – från tömning till återvinning. Fyll i formuläret och se priset direkt. Inga offertförfrågningar, inga dolda avgifter.\n\nDu kan även ringa oss på 08-517 082 81 för att boka din förrådstömning.",
          cta: { text: "Boka förrådstömning", href: "#priser" },
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar det att tömma ett förråd?",
        answer:
          "Priset beror på förrådets storlek, vad som finns i det och hur tillgängligt det är. Ett litet förråd med kartonger och småprylar kan kosta från 595 kr (Extra Liten). Ett mellanstort förråd med möbler och blandade saker landar vanligtvis på 1 495–2 495 kr. Du ser priset direkt vid bokning – inga överraskningar. Avfallsavgifter tillkommer separat och ligger oftast runt 400 kr.",
      },
      {
        question: "Kan ni tömma förråd på externa förrådsanläggningar?",
        answer:
          "Ja, vi tömmer regelbundet förråd på anläggningar som Shurgard, 24Storage, Pelican Self Storage, Green Storage, Storage365 och lokala alternativ. Vi behöver bara adressen och eventuella tillträdeskoder. Kontrollera med din anläggning om besökare behöver anmälas i förväg.",
      },
      {
        question: "Behöver jag vara på plats vid förrådstömningen?",
        answer:
          "Nej, du behöver inte vara på plats. Ge oss nyckel, portkod eller hänglåskombination så sköter vi resten. Många kunder lämnar bara tillträdesinformation via appen och låter oss jobba självständigt. Om du vill sortera ut saker att behålla är det dock praktiskt att vara där i början.",
      },
      {
        question: "Hur lång tid tar det att tömma ett förråd?",
        answer:
          "Det beror på storleken och innehållet. Ett förråd på 2–4 kvm med mest kartonger tar vanligtvis 20–40 minuter. Ett förråd på 6–10 kvm fullpackat med möbler och vitvaror kan ta 1–3 timmar. Vi jobbar effektivt och har rätt antal ungdomar på plats.",
      },
      {
        question: "Kan ni tömma ett förråd inför uppsägning av hyresavtal?",
        answer:
          "Absolut – det är en av de vanligaste anledningarna till att folk bokar oss. Vi kan ofta komma samma dag eller nästa dag, så att du hinner tömma förrådet innan avtalets slutdatum. Berätta när din deadline är vid bokning, så prioriterar vi uppdraget.",
      },
      {
        question: "Vad händer med sakerna efter förrådstömningen?",
        answer:
          "Vi sorterar allt noggrant. Saker i bra skick skänks vidare till organisationer som Myrorna och Stadsmissionen. Elektronik lämnas till godkänd e-avfallsinsamling. Övrigt material sorteras och körs till godkänd återvinningsstation. Vårt mål är alltid att minimera det som hamnar på deponi.",
      },
      {
        question: "Kan jag få RUT-avdrag på förrådstömning?",
        answer:
          "Om sakerna från ditt förråd transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Vanlig förrådstömning där allt körs direkt till återvinning ger däremot inte rätt till RUT-avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Kan ni tömma BRF-förråd i källarplan?",
        answer:
          "Ja, vi tömmer dagligen BRF-förråd i källarplan runt om i Stockholm och Göteborg. Våra ungdomar är vana vid smala korridorer, tunga lyft i trappor och galler- eller nätförråd. Vi ser till att gemensamma ytor lämnas rena efter tömningen.",
      },
      {
        question: "Tar ni hand om farligt avfall från förråd?",
        answer:
          "Vi kan inte hantera farligt avfall som lösningsmedel, kemikalier eller tryckbehållare. Däremot tar vi med oss allt annat – inklusive elektronik, färgburkar och lättare byggavfall. Är du osäker? Beskriv innehållet vid bokning, så berättar vi vad vi kan ta med.",
      },
      {
        question: "Hur snabbt kan ni tömma mitt förråd?",
        answer:
          "Vi erbjuder samdagsservice och kan ofta vara på plats inom 60 minuter vid akuta behov. Du kan även planera upp till 30 dagar i förväg. Välj tid direkt i bokningsformuläret – du ser tillgängliga tider och pris på en gång.",
      },
      {
        question: "Kan ni tömma ett förråd som hör till ett dödsbo?",
        answer:
          "Ja, vi hjälper regelbundet till med att tömma förråd i samband med dödsbon. Våra ungdomar hanterar allt varsamt, sorterar noggrant och ser till att saker i bra skick tas om hand. Du behöver inte vara på plats om du inte vill.",
      },
      {
        question: "Kan ni tömma ett förråd med okänt innehåll?",
        answer:
          "Ja, vi tömmer regelbundet förråd där ingen vet exakt vad som finns innanför. Våra ungdomar inventerar innehållet på plats, sorterar och kör bort allt. Skicka gärna en bild på förrådet i förväg så kan vi ge en bättre prisuppskattning redan vid bokning.",
      },
      {
        question: "Kan ni tömma förrådet om jag inte bor i närheten?",
        answer:
          "Absolut. Ge oss tillgång via nyckel, portkod eller en kontaktperson på plats, så sköter vi hela förrådstömningen utan att du behöver vara där. Vi skickar uppdateringar under arbetets gång så att du har full koll.",
      },
      {
        question: "Vilka områden erbjuder ni förrådstömning i?",
        answer:
          "Vi erbjuder förrådstömning i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Töm ditt förråd i 3 enkla steg",
      step2Text:
        "Våra ungdomar åker direkt till förrådet, bär ut allt och lastar fordonet. Oavsett om förrådet är i en källare, på en anläggning eller i ett garage – vi löser det.",
      ctaHeading: "Boka förrådstömning idag",
      ctaText:
        "Fyll i adressen till förrådet, beskriv innehållet och välj en tid. Du ser priset direkt – slipp betala en månad extra i hyra.",
    },

    en: {
      slug: "storage-unit-clearance",

      featureCards: {
        sinceText: "Storage clearance since 2014",
        bookYourText: "Book your storage clearance",
        bookDescText:
          "Enter the address of your storage unit, describe what's inside, and pick a time that suits you. You see the price instantly.",
        vehicleTitleText: "The right vehicle for your storage clearance",
        vehicleDescText:
          "Small unit or large? We match the vehicle to your job – from a van for a few boxes to a truck with a tail lift for heavy furniture and appliances.",
      },

      meta: {
        title: "Storage Unit Clearance – Fast & Affordable | Ungdomskraft",
        description:
          "Book storage unit clearance quickly and easily. We empty your unit, carry everything out and take it to recycling. Perfect when your lease is ending.",
        h1: "Storage unit clearance",
      },

      hero: {
        heading: "Help clearing your storage unit",
        intro:
          "Storage unit overflowing and the lease deadline approaching? We clear the entire unit for you – whether it's in a basement, at a self-storage facility or in a garage. Our young workers carry everything out, sort it and haul it away. You just give us access.",
      },

      itemsSection: {
        heading: "Items we collect from storage units",
        items: [
          {
            name: "Moving boxes",
            description: "Unpacked boxes and crates from your last move",
            image: "/box.webp",
          },
          {
            name: "Seasonal storage",
            description: "Ski and camping gear, winter tyres and seasonal clothing",
            image: "/fitness-equipment.webp",
          },
          {
            name: "Furniture",
            description: "Sofas, beds, tables and chairs waiting for a new home",
            image: "/sofa.webp",
          },
          {
            name: "Appliances",
            description: "Spare fridges, freezers and washing machines",
            image: "/fridge.webp",
          },
          {
            name: "Electronics",
            description: "Old computers, monitors, speakers and cables",
            image: "/tv.webp",
          },
          {
            name: "Miscellaneous",
            description: "Everything else – tools, ornaments, kids' stuff and kitchenware",
            image: "/appliances.webp",
          },
        ],
      },

      seoContent: {
        mainHeading: "Hassle-free storage unit clearance",
        mainText:
          "It usually starts with a box or two that's \"only staying temporarily\". A few years later the storage unit is packed to the ceiling with things you barely remember owning. The rent keeps ticking every month, and the thought of clearing it all out on your own feels hopeless – especially when the unit is at a different address.\n\nWith Ungdomskraft we handle the entire process. We drive to your storage unit, carry everything out, sort what can be donated or recycled and haul the rest away. You save time, avoid heavy lifting and can cancel your rental agreement without the stress. Whether your unit is at Shurgard, 24Storage or in the building's basement, we do the work for you.",
        preparationHeading: "Prepare for your storage clearance",
        preparationText:
          "Make sure we can get into the unit – leave a key, entry code or padlock combination. If the unit is at an external facility, check that we're allowed to collect items (some facilities require visitors to be registered in advance). Want to keep something? Mark it clearly or point it out on site, and we'll leave it behind.",
        additionalSections: [
          {
            heading: "Why book storage clearance with Ungdomskraft?",
            listItems: [
              {
                label: "Stop paying rent you don't need to –",
                text: "Every month you delay the clearance costs money. We can often be on site the same day or next day, so you can cancel your storage contract before the next billing period. Book online or call us at +46 8-517 082 81.",
              },
              {
                label: "We come to the facility –",
                text: "You don't even need to transport items home first. We drive straight to Shurgard, 24Storage, Pelican Self Storage, Green Storage or wherever your unit is – and clear it on site.",
              },
              {
                label: "Sorting and recycling included –",
                text: "We don't throw everything into a skip. Items in good condition go to charity shops, electronics go to approved e-waste collection, and the rest is sorted at the recycling station. You may be eligible for the Swedish RUT tax deduction if items are transported for reuse.",
              },
              {
                label: "Insured through Trygg-Hansa –",
                text: "Every job is fully insured. Your belongings and the property are protected throughout the clearance – from lifting to transport.",
              },
            ],
          },
          {
            heading: "Common reasons people clear their storage units",
            content:
              "We help private individuals and businesses in all sorts of situations. Here are the most common reasons our customers book a storage clearance:",
            listItems: [
              {
                label: "Moving –",
                text: "you're changing city or home and don't want to bring everything that's piled up in storage",
              },
              {
                label: "Lease ending –",
                text: "the deadline is approaching and the unit must be empty before the contract expires",
              },
              {
                label: "Seasonal declutter –",
                text: "you want to clear out everything you haven't used in over a year to free up space",
              },
              {
                label: "Estate clearance –",
                text: "a storage unit belonging to an estate that needs emptying as part of probate",
              },
              {
                label: "Downsizing –",
                text: "you want to switch to a smaller, cheaper unit and need help clearing the excess",
              },
              {
                label: "Building renovation –",
                text: "the housing association is renovating and all residents must empty their units",
              },
            ],
            afterContent:
              "Whatever the reason, we make it easy. Describe what's in the unit when booking and we'll send the right vehicle and the right number of workers.",
          },
          {
            heading: "How storage clearance works with Ungdomskraft",
            listItems: [
              {
                label: "1. Describe your unit –",
                text: "Enter the address of the storage facility, tell us roughly how much needs to go and pick a date. You get an instant price – no hidden costs.",
              },
              {
                label: "2. We clear the unit –",
                text: "Our workers drive to the unit, open it, carry everything out and load the vehicle. Whether it's in a basement, a storage box or a garage, we get it done efficiently and carefully.",
              },
              {
                label: "3. Sorting and recycling –",
                text: "Anything that can be reused is delivered to charity. Electronics go to e-waste. The rest is taken to an approved recycling station and sorted there.",
              },
            ],
          },
          {
            heading: "We work with all types of storage units",
            content:
              "Our workers have cleared hundreds of storage units – from small caged units in cramped basement corridors to large self-storage boxes at modern facilities. We know the rules at most facilities in Stockholm and Gothenburg, and we plan the job so it runs quickly and smoothly.\n\nSize doesn't matter. A small 2 sqm unit with just boxes might take 20 minutes. A large unit packed with furniture, bikes and appliances can take a couple of hours. We give you an exact price at booking regardless of size.",
          },
          {
            heading: "Book storage clearance today",
            content:
              "Stop paying rent for a storage unit you don't need. Book a clearance with Ungdomskraft and we take care of the rest – from emptying to recycling. Fill in the form and see the price instantly. No quote requests, no hidden fees.\n\nYou can also call us at +46 8-517 082 81 to book your storage clearance.",
            cta: { text: "Book storage clearance", href: "#priser" },
          },
        ],
      },

      faq: [
        {
          question: "How much does it cost to clear a storage unit?",
          answer:
            "The price depends on the size of the unit, what's inside and how accessible it is. A small unit with boxes and small items can cost from SEK 595 (Extra Liten). A medium-sized unit with furniture and mixed items usually comes to SEK 1,495–2,495. You see the price instantly when booking – no surprises. Waste disposal fees are charged separately and usually run around SEK 400.",
        },
        {
          question: "Can you clear units at external self-storage facilities?",
          answer:
            "Yes, we regularly clear units at facilities like Shurgard, 24Storage, Pelican Self Storage, Green Storage, Storage365 and local alternatives. We just need the address and any access codes. Check with your facility if visitors need to be registered in advance.",
        },
        {
          question: "Do I need to be present during the clearance?",
          answer:
            "No, you don't need to be there. Give us a key, entry code or padlock combination and we handle the rest. Many customers simply leave access details through the app and let us work independently. If you want to pick out items to keep, it's practical to be there at the start.",
        },
        {
          question: "How long does it take to clear a storage unit?",
          answer:
            "It depends on the size and contents. A 2–4 sqm unit with mostly boxes usually takes 20–40 minutes. A 6–10 sqm unit packed with furniture and appliances can take 1–3 hours. We work efficiently and bring the right number of workers.",
        },
        {
          question: "Can you clear a unit before my lease expires?",
          answer:
            "Absolutely – that's one of the most common reasons people book us. We can often come the same day or next day, so you can empty the unit before the contract end date. Let us know your deadline when booking and we'll prioritise the job.",
        },
        {
          question: "What happens to the items after the clearance?",
          answer:
            "We sort everything carefully. Items in good condition are donated to charity organisations. Electronics go to approved e-waste collection. Other materials are sorted and taken to an approved recycling station. Our goal is always to minimise what ends up in landfill.",
        },
        {
          question: "Am I eligible for a RUT tax deduction on storage clearance?",
          answer:
            "If the items from your unit are transported to a charity shop or organisation for reuse, you're eligible for the RUT deduction on the labour cost. A standard clearance where everything goes straight to recycling does not qualify. The deduction is applied directly on the invoice.",
        },
        {
          question: "Can you clear storage cages in apartment building basements?",
          answer:
            "Yes, we clear storage cages in apartment basements every day across Stockholm and Gothenburg. Our workers are used to narrow corridors, heavy lifting on stairs and mesh or cage units. We make sure shared areas are left clean after the job.",
        },
        {
          question: "Do you handle hazardous waste from storage units?",
          answer:
            "We can't handle hazardous waste such as solvents, chemicals or pressurised containers. However, we take everything else – including electronics, paint cans and lighter construction waste. Not sure? Describe the contents when booking and we'll tell you what we can take.",
        },
        {
          question: "How quickly can you clear my storage unit?",
          answer:
            "We offer same-day service and can often be on site within 60 minutes for urgent needs. You can also plan up to 30 days ahead. Pick a time directly in the booking form – you see available slots and prices straight away.",
        },
        {
          question: "Can you clear a storage unit that's part of an estate?",
          answer:
            "Yes, we regularly help clear storage units in connection with estate clearances. Our workers handle everything with care, sort thoroughly and make sure items in good condition are taken care of. You don't need to be present if you'd rather not.",
        },
        {
          question: "Can you clear a storage unit with unknown contents?",
          answer:
            "Yes, we regularly clear storage units where nobody knows exactly what's inside. Our workers inventory the contents on site, sort everything and haul it all away. Send us a photo of the unit in advance and we can give a more accurate price estimate at booking.",
        },
        {
          question: "Can you clear my storage unit if I don't live nearby?",
          answer:
            "Absolutely. Give us access via a key, entry code or a contact person on site, and we handle the entire storage clearance without you being there. We send updates during the job so you stay fully informed.",
        },
        {
          question: "Which areas do you offer storage clearance in?",
          answer:
            "We offer storage clearance throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,

      pricingRows: getPackagePricingRows("bortforsling", "en"),

      dynamicHeadings: {
        threeSteps: "Clear your storage unit in 3 easy steps",
        step2Text:
          "Our workers drive straight to the unit, carry everything out and load the vehicle. Whether the unit is in a basement, at a facility or in a garage – we handle it.",
        ctaHeading: "Book storage clearance today",
        ctaText:
          "Enter the address of your unit, describe the contents and pick a time. You see the price instantly – stop paying an extra month of rent.",
      },
    },
  },

  {
    // Tömma lägenhet — config för /tomma-lagenhet
    // Vinkel: Komplett lägenhetsröjning — allt från bohag till grovsopor.
    // Unik vinkel vs källare/förråd: hela bostaden, inflyttning/utflyttning,
    // hyresvärd/BRF-krav, tidskritiskt vid uthyrning, dödsbotömning.
    slug: "tomma-lagenhet",
    serviceCategory: "tömningar",

    meta: {
      title: "Tömma lägenhet – Komplett Lägenhetsröjning | Ungdomskraft",
      description:
        "Boka lägenhetsröjning i Stockholm och Göteborg. Vi tömmer hela lägenheten – möbler, vitvaror, kläder och allt annat. Fast pris, försäkrat. Boka idag!",
      h1: "Tömma lägenhet",
    },

    hero: {
      heading: "Hjälp att tömma lägenhet",
      intro:
        "Behöver du tömma en hel lägenhet? Vi tar hand om allt – från möbler och vitvaror till gardiner, husgeråd och grovsopor. Oavsett om det gäller en utflyttning, dödsbo eller renovering ser våra ungdomar till att lägenheten lämnas tom och klar. Du visar vad som ska bort, vi gör resten.",
      heroImage: "/hero-bortforsling.webp",
    },

    featureCards: {
      headingText: "Vi sköter din lägenhetsröjning",
      sinceText: "Lägenhetsröjning sedan 2014",
      bookYourText: "Boka din lägenhetsröjning",
      bookDescText:
        "Ange lägenhetens adress, beskriv ungefär vad som finns kvar, och välj en tid som passar. Du ser priset direkt.",
      vehicleTitleText: "Anpassat fordon för din lägenhetsröjning",
      vehicleDescText:
        "Vi matchar fordonet efter lägenhetens storlek och innehåll – från skåpbil för en etta till lastbil med bakgavellyft för en större bostad med tungt bohag.",
    },

    itemsSection: {
      heading: "Vi tömmer hela lägenheten",
      items: [
        {
          name: "Möbler",
          description: "Soffor, sängar, garderober, bord, stolar och hyllor",
          image: "/sofa.webp",
        },
        {
          name: "Vitvaror",
          description: "Kylskåp, frysar, spis, tvättmaskin och diskmaskin",
          image: "/fridge.webp",
        },
        {
          name: "Textilier & kläder",
          description: "Gardiner, mattor, sängkläder, handdukar och kläder",
          image: "/bedding.webp",
        },
        {
          name: "Husgeråd",
          description: "Porslin, bestick, köksredskap, krukor och prydnader",
          image: "/dishes.webp",
        },
        {
          name: "Elektronik",
          description: "TV, datorer, ljudsystem och kablar",
          image: "/tv.webp",
        },
        {
          name: "Övrigt",
          description: "Böcker, leksaker, verktyg, tavlor och allt annat",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Komplett lägenhetsröjning utan stress",
      mainText:
        "Att tömma en hel lägenhet är ett stort projekt. Det handlar inte bara om möbler – det är vitvaror som ska kopplas loss, lådor fulla med porslin, garderober med kläder, badrum med produkter och allt det där som samlats i skåp och på hyllor genom åren. De flesta underskattar hur lång tid det tar och hur mycket som faktiskt finns.\n\nMed Ungdomskraft slipper du hantera det själv. Vi skickar ett team av unga, starka medarbetare som systematiskt går igenom varje rum. De monterar isär möbler, kopplar loss vitvaror, packar ner lösa saker och bär ut allt. Det som kan skänkas vidare levererar vi till organisationer som Myrorna och Stadsmissionen – resten körs till godkänd återvinningsstation. När vi är klara lämnar vi lägenheten tom och redo för nästa steg.",
      preparationHeading: "Förbered inför din lägenhetsröjning",
      preparationText:
        "Gå igenom lägenheten och märk upp saker du vill behålla – allt annat tar vi med. Kontrollera att el och vatten fungerar om vitvaror ska kopplas loss. Se till att vi har tillgång till alla rum, inklusive förråd som hör till lägenheten. Bor du i ett flerfamiljshus? Kolla om det finns regler kring hiss- eller trappanvändning vid större tömningar. Behöver lägenheten vara tom till ett visst datum? Berätta det vid bokning så planerar vi därefter.",
      additionalSections: [
        {
          heading: "Varför välja Ungdomskraft för lägenhetsröjning?",
          listItems: [
            {
              label: "Vi tar allt – inte bara möbler –",
              text: "Till skillnad från vanlig bortforsling tömmer vi hela lägenheten. Köksskåp, klädkammare, badrumsskåp och garderober – vi tömmer varenda hörn. Du lämnar oss en full lägenhet och får tillbaka en tom.",
            },
            {
              label: "Anpassad bemanning –",
              text: "Vi dimensionerar teamet efter lägenhetens storlek. En etta kräver vanligtvis 2 ungdomar, en trea eller större bostad kan behöva 3–4. Vi ser till att uppdraget har rätt bemanning från start.",
            },
            {
              label: "Snabb genomförande –",
              text: "Vi vet att tid ofta är kritiskt vid lägenhetsröjningar – kanske ska en ny hyresgäst flytta in, eller lägenheten ska säljas. Våra ungdomar jobbar effektivt och vi erbjuder ofta samdags- eller nästadagsservice.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg-Hansa. Golv, väggar, trapphus och hissar skyddas under hela processen – du kan lämna över ansvaret till oss med gott samvete.",
            },
          ],
        },
        {
          heading: "Vanliga situationer för lägenhetsröjning",
          content:
            "Vi hjälper privatpersoner, hyresvärdar och bostadsrättsföreningar med alla typer av lägenhetsröjningar:",
          listItems: [
            {
              label: "Utflyttning –",
              text: "du flyttar och vill inte ta med allt till den nya bostaden. Vi tömmer det du lämnar kvar",
            },
            {
              label: "Dödsbo –",
              text: "en anhörig har gått bort och lägenheten behöver tömmas. Vi hanterar allt varsamt och respektfullt",
            },
            {
              label: "Renovering –",
              text: "lägenheten ska totalrenoveras och allt bohag måste bort innan hantverkarna börjar",
            },
            {
              label: "Hyresvärd –",
              text: "en hyresgäst har lämnat saker kvar efter utflyttning och lägenheten behöver tömmas inför ny uthyrning",
            },
            {
              label: "Försäljning –",
              text: "lägenheten ska visas eller säljas och behöver vara tom för att ge bästa intryck",
            },
            {
              label: "Separation –",
              text: "en av parterna har flyttat och kvarvarande bohag ska bort",
            },
          ],
          afterContent:
            "Oavsett anledning hanterar vi uppdraget professionellt och diskret. Beskriv situationen vid bokning så anpassar vi upplägget.",
        },
        {
          heading: "Så går en lägenhetsröjning till",
          listItems: [
            {
              label: "1. Beskriv lägenheten –",
              text: "Fyll i adress, antal rum, ungefärlig mängd bohag och önskat datum. Du får ett pris direkt – inga offertförfrågningar.",
            },
            {
              label: "2. Vi tömmer rum för rum –",
              text: "Våra ungdomar arbetar systematiskt genom lägenheten. Möbler monteras isär, vitvaror kopplas loss och allt bärs ut och lastas. Trapphus, hissar och golv skyddas under hela processen.",
            },
            {
              label: "3. Sortering och återvinning –",
              text: "Allt sorteras noggrant. Kläder, husgeråd och möbler i bra skick skänks till välgörenhet. Elektronik lämnas till e-avfall. Övrigt körs till godkänd återvinningsstation.",
            },
          ],
        },
        {
          heading: "Erfarenhet av alla typer av lägenheter",
          content:
            "Vi har röjt hundratals lägenheter – från kompakta ettor i innerstaden till rymliga fyror i förorterna. Våra ungdomar vet hur man hanterar tunga soffor i trapphus utan hiss, hur man skyddar parkettgolv vid tunga lyft, och hur man lastar ett helt bohag på ett enda lass.\n\nBehöver lägenheten tömmas samma dag? Vi kan ofta erbjuda akuttjänst med kort varsel. Har du gott om tid? Boka i förväg och välj den tid som passar dig bäst – vi finns tillgängliga alla dagar i veckan.",
        },
        {
          heading: "Boka lägenhetsröjning idag",
          content:
            "Ge oss en full lägenhet – vi ger tillbaka den tom. Med Ungdomskraft behöver du inte tänka på tunga lyft, sortering eller återvinning. Vi sköter allt från ax till limpa. Fyll i formuläret och se priset direkt.\n\nDu kan även ringa oss på 08-517 082 81 för att boka din lägenhetsröjning.",
          cta: { text: "Boka lägenhetsröjning", href: "#priser" },
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar det att tömma en lägenhet?",
        answer:
          "Priset beror på lägenhetens storlek, mängden bohag och hur lång tid uppdraget tar. En etta med normalt bohag landar oftast på 2 495–4 995 kr. En trea eller större lägenhet kan kosta uppåt 3 000–5 000 kr beroende på mängd. Du får ett pris direkt vid bokning – inga dolda avgifter. Avfallsavgifter tillkommer separat.",
      },
      {
        question: "Hur lång tid tar det att tömma en lägenhet?",
        answer:
          "En etta tar vanligtvis 2–4 timmar. En tvåa eller trea tar 4–6 timmar. Större lägenheter eller bostäder med mycket bohag kan ta en hel dag. Vi anpassar teamets storlek efter uppdraget så att det går så snabbt som möjligt utan att kvaliteten påverkas.",
      },
      {
        question: "Tömmer ni även kök och badrum?",
        answer:
          "Ja, vi tömmer alla rum – inklusive kök, badrum, förråd, balkong och klädkammare. Vi tömmer ut skåp, lådor och hyllor och tar med allt som ska bort. Vitvaror som spis, kylskåp, diskmaskin och tvättmaskin kopplar vi loss och bär ut om du vill.",
      },
      {
        question: "Kan ni montera isär möbler vid lägenhetsröjning?",
        answer:
          "Ja, våra ungdomar monterar isär garderober, sängar, skrivbord och andra möbler som behöver demonteras för att komma ut. De har med sig standardverktyg. Om möblerna kräver specialverktyg ber vi dig ha dessa redo.",
      },
      {
        question: "Behöver jag vara hemma under lägenhetsröjningen?",
        answer:
          "Du behöver inte vara hemma under hela processen, men det är bra att vara på plats i början för att visa vad som ska bort och peka ut eventuella saker du vill behålla. Alternativt kan du märka det du vill spara och ge oss nyckel – vi sköter resten.",
      },
      {
        question: "Tömmer ni lägenheter inför renoveringar?",
        answer:
          "Absolut. Vi hjälper regelbundet till med att tömma lägenheter inför totalrenoveringar, stambyte eller köksbyten. Vi bär ut allt bohag och grovsopor så att hantverkarna kan komma igång direkt utan fördröjning.",
      },
      {
        question: "Kan ni tömma en lägenhet som är ett dödsbo?",
        answer:
          "Ja, vi har stor erfarenhet av att tömma lägenheter i samband med dödsbon. Våra ungdomar hanterar allt varsamt och respektfullt. Saker i bra skick skänks vidare, personliga tillhörigheter av värde lägger vi åt sidan om du önskar, och resten hanteras ansvarsfullt.",
      },
      {
        question: "Kan jag få RUT-avdrag på lägenhetsröjning?",
        answer:
          "Om bohaget transporteras till en secondhand-butik, välgörenhetsorganisation eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Vanlig lägenhetsröjning där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Vad händer med kläderna och småsakerna?",
        answer:
          "Kläder i bra skick sorteras och skänks till organisationer som Stadsmissionen och Myrorna. Kläder som inte går att skänka lämnas till textilåtervinning. Småsaker som porslin, köksredskap och böcker som är i gott skick skänks vidare. Resten återvinns eller körs till godkänd avfallsstation.",
      },
      {
        question: "Ingår avfallsavgifter i priset?",
        answer:
          "Nej, avfalls- och deponiavgifter debiteras separat och varierar beroende på återvinningsstation samt vikt och typ av avfall. Vid en komplett lägenhetsröjning brukar avgiften ligga på 500–1 000 kr. Du får alltid en uppskattning innan vi påbörjar arbetet.",
      },
      {
        question: "Hur snabbt kan ni tömma min lägenhet?",
        answer:
          "Vi erbjuder samdagsservice och kan ofta vara på plats inom kort. Har du ont om tid inför en utflyttning eller hyresgästbyte? Berätta din deadline vid bokning så prioriterar vi uppdraget. Du kan boka med 30 minuters varsel eller planera upp till 30 dagar framåt.",
      },
      {
        question: "Kan ni tömma en lägenhet utan hiss?",
        answer:
          "Ja, våra ungdomar är vana vid att bära möbler och bohag i trappor, även i äldre flervåningshus utan hiss. Vi anpassar teamets storlek efter våningsplan och mängd så att lägenhetsröjningen går smidigt och effektivt oavsett hur högt upp du bor.",
      },
      {
        question: "Sorterar ni saker som kan återanvändas vid lägenhetsröjning?",
        answer:
          "Ja, vi sorterar allt under tömningen. Möbler, kläder och husgeråd i bra skick skänks till organisationer som Myrorna och Stadsmissionen. Elektronik lämnas till godkänd e-avfallsmottagning och resten körs till återvinningsstation. Du kan även ha rätt till RUT-avdrag när saker transporteras till återanvändning.",
      },
      {
        question: "Vilka områden erbjuder ni lägenhetsröjning i?",
        answer:
          "Vi erbjuder lägenhetsröjning i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Töm din lägenhet i 3 enkla steg",
      step2Text:
        "Våra ungdomar arbetar systematiskt genom varje rum – monterar isär möbler, kopplar loss vitvaror och bär ut allt. Trapphus och golv skyddas under hela processen.",
      ctaHeading: "Boka lägenhetsröjning idag",
      ctaText:
        "Fyll i adressen, beskriv lägenheten och välj en tid. Du ser priset direkt – ge oss en full lägenhet, vi ger tillbaka den tom.",
    },

    en: {
      slug: "apartment-clearance",

      featureCards: {
        sinceText: "Apartment clearance since 2014",
        bookYourText: "Book your apartment clearance",
        bookDescText:
          "Enter the apartment address, describe roughly what's left inside, and pick a time that suits you. You see the price instantly.",
        vehicleTitleText: "The right vehicle for your apartment clearance",
        vehicleDescText:
          "We match the vehicle to the size of the apartment and its contents – from a van for a studio to a truck with tail lift for a larger home with heavy furnishings.",
      },

      meta: {
        title: "Apartment Clearance – Complete & Affordable | Ungdomskraft",
        description:
          "Book apartment clearance quickly and easily. We empty the entire apartment – furniture, appliances, clothes and everything else. Fixed price, often same day.",
        h1: "Apartment clearance",
      },

      hero: {
        heading: "Help clearing your apartment",
        intro:
          "Need to clear out an entire apartment? We handle everything – from furniture and appliances to curtains, kitchenware and bulky waste. Whether it's a move-out, estate clearance or renovation, our young workers leave the apartment empty and ready. You show us what goes, we do the rest.",
      },

      itemsSection: {
        heading: "We clear the entire apartment",
        items: [
          {
            name: "Furniture",
            description: "Sofas, beds, wardrobes, tables, chairs and shelves",
            image: "/sofa.webp",
          },
          {
            name: "Appliances",
            description: "Fridge, freezer, stove, washing machine and dishwasher",
            image: "/fridge.webp",
          },
          {
            name: "Textiles & clothing",
            description: "Curtains, rugs, bedding, towels and clothes",
            image: "/bedding.webp",
          },
          {
            name: "Kitchenware",
            description: "China, cutlery, utensils, pots and ornaments",
            image: "/dishes.webp",
          },
          {
            name: "Electronics",
            description: "TVs, computers, audio systems and cables",
            image: "/tv.webp",
          },
          {
            name: "Everything else",
            description: "Books, toys, tools, wall art and anything left behind",
            image: "/box.webp",
          },
        ],
      },

      seoContent: {
        mainHeading: "Complete apartment clearance without the stress",
        mainText:
          "Clearing an entire apartment is a big project. It's not just furniture – it's appliances that need disconnecting, drawers full of china, wardrobes of clothes, bathrooms of products and all the things that have accumulated in cupboards and on shelves over the years. Most people underestimate how long it takes and how much there actually is.\n\nWith Ungdomskraft you don't have to deal with any of it. We send a team of young, strong workers who systematically go through every room. They disassemble furniture, disconnect appliances, pack up loose items and carry everything out. Items that can be donated are delivered to charity organisations – the rest goes to an approved recycling station. When we're done, you get back an empty apartment ready for whatever comes next.",
        preparationHeading: "Prepare for your apartment clearance",
        preparationText:
          "Walk through the apartment and mark anything you want to keep – we take everything else. Check that electricity and water are working if appliances need disconnecting. Make sure we have access to all rooms, including any storage that belongs to the apartment. Live in an apartment building? Check if there are rules about lift or stairwell use during larger clearances. Need the apartment empty by a certain date? Let us know when booking and we'll plan accordingly.",
        additionalSections: [
          {
            heading: "Why choose Ungdomskraft for apartment clearance?",
            listItems: [
              {
                label: "We take everything – not just furniture –",
                text: "Unlike standard junk removal, we clear the entire apartment. Kitchen cabinets, walk-in closets, bathroom shelves and wardrobes – we empty every corner. You hand us a full apartment and get back an empty one.",
              },
              {
                label: "Right-sized team –",
                text: "We scale the team to the apartment's size. A studio typically needs 2 workers, a 3-bedroom home may need 3–4. We make sure the job has the right crew from the start.",
              },
              {
                label: "Fast turnaround –",
                text: "We know time is often critical with apartment clearances – maybe a new tenant is moving in, or the apartment is going on the market. Our workers are efficient and we often offer same-day or next-day service.",
              },
              {
                label: "Insured and secure –",
                text: "Every job is insured through Trygg-Hansa. Floors, walls, stairwells and lifts are protected throughout the process – you can hand over responsibility with complete peace of mind.",
              },
            ],
          },
          {
            heading: "Common situations for apartment clearance",
            content:
              "We help private individuals, landlords and housing associations with all types of apartment clearances:",
            listItems: [
              {
                label: "Moving out –",
                text: "you're relocating and don't want to bring everything to the new place. We clear what you leave behind",
              },
              {
                label: "Estate clearance –",
                text: "a family member has passed away and the apartment needs emptying. We handle everything with care and respect",
              },
              {
                label: "Renovation –",
                text: "the apartment is being fully renovated and all furnishings must go before the contractors start",
              },
              {
                label: "Landlord –",
                text: "a tenant has left belongings behind after moving out and the apartment needs clearing before the next lease",
              },
              {
                label: "Property sale –",
                text: "the apartment needs to be empty for viewings or to make the best impression on buyers",
              },
              {
                label: "Separation –",
                text: "one party has moved out and the remaining furnishings need to go",
              },
            ],
            afterContent:
              "Whatever the situation, we handle the job professionally and discreetly. Describe the circumstances when booking and we'll tailor the approach.",
          },
          {
            heading: "How apartment clearance works",
            listItems: [
              {
                label: "1. Describe the apartment –",
                text: "Enter the address, number of rooms, approximate amount of furnishings and your preferred date. You get an instant price – no quote requests.",
              },
              {
                label: "2. We clear room by room –",
                text: "Our workers move systematically through the apartment. Furniture is disassembled, appliances disconnected and everything is carried out and loaded. Stairwells, lifts and floors are protected throughout.",
              },
              {
                label: "3. Sorting and recycling –",
                text: "Everything is carefully sorted. Clothes, kitchenware and furniture in good condition go to charity. Electronics go to e-waste. The rest is taken to an approved recycling station.",
              },
            ],
          },
          {
            heading: "Experience with all types of apartments",
            content:
              "We've cleared hundreds of apartments – from compact studios in the city centre to spacious family homes in the suburbs. Our workers know how to handle heavy sofas in stairwells without lifts, how to protect parquet floors during heavy lifting, and how to load an entire household in a single trip.\n\nNeed the apartment cleared today? We can often provide urgent service at short notice. Got plenty of time? Book ahead and choose the slot that suits you best – we're available every day of the week.",
          },
          {
            heading: "Book apartment clearance today",
            content:
              "Give us a full apartment – we give it back empty. With Ungdomskraft you don't need to think about heavy lifting, sorting or recycling. We handle everything from start to finish. Fill in the form and see the price instantly.\n\nYou can also call us at +46 8-517 082 81 to book your apartment clearance.",
            cta: { text: "Book apartment clearance", href: "#priser" },
          },
        ],
      },

      faq: [
        {
          question: "How much does it cost to clear an apartment?",
          answer:
            "The price depends on the apartment's size, the amount of furnishings and how long the job takes. A studio with standard furnishings usually comes to SEK 2,495–4,995. A two- or three-bedroom apartment can cost upwards of SEK 3,000–5,000 depending on volume. You get an instant price when booking – no hidden fees. Waste disposal fees are charged separately.",
        },
        {
          question: "How long does it take to clear an apartment?",
          answer:
            "A studio usually takes 2–4 hours. A two- or three-bedroom apartment takes 4–6 hours. Larger homes or apartments with a lot of furnishings can take a full day. We scale the team size to the job so it runs as quickly as possible without compromising quality.",
        },
        {
          question: "Do you clear the kitchen and bathroom too?",
          answer:
            "Yes, we clear every room – including kitchen, bathroom, storage, balcony and walk-in closets. We empty out cabinets, drawers and shelves and take everything that needs to go. Appliances like the stove, fridge, dishwasher and washing machine can be disconnected and removed if you wish.",
        },
        {
          question: "Can you disassemble furniture during the clearance?",
          answer:
            "Yes, our workers disassemble wardrobes, beds, desks and other furniture that needs taking apart to get out. They bring standard tools. If your furniture requires special tools, please have them ready.",
        },
        {
          question: "Do I need to be home during the apartment clearance?",
          answer:
            "You don't need to be there for the entire process, but it's helpful to be present at the start to show what should go and point out anything you want to keep. Alternatively, you can mark items to keep and give us the key – we handle the rest.",
        },
        {
          question: "Can you clear an apartment that's part of an estate?",
          answer:
            "Yes, we have extensive experience clearing apartments in connection with estates. Our workers handle everything with care and respect. Items in good condition are donated, personal valuables are set aside if you wish, and the rest is handled responsibly.",
        },
        {
          question: "Am I eligible for a RUT tax deduction on apartment clearance?",
          answer:
            "If the furnishings are transported to a charity shop or organisation for reuse, you're eligible for the RUT deduction on the labour cost. A standard clearance where everything goes straight to a recycling station does not qualify. The deduction is applied directly on the invoice.",
        },
        {
          question: "What happens to clothes and small items?",
          answer:
            "Clothes in good condition are sorted and donated to organisations like Stadsmissionen and Myrorna. Clothes that can't be donated go to textile recycling. Small items like china, kitchen utensils and books in good condition are donated. The rest is recycled or taken to an approved waste station.",
        },
        {
          question: "Are waste disposal fees included in the price?",
          answer:
            "No, waste and landfill fees are charged separately and vary depending on the recycling station, weight and type of waste. For a complete apartment clearance the fee usually runs SEK 500–1,000. You always get an estimate before we start.",
        },
        {
          question: "How quickly can you clear my apartment?",
          answer:
            "We offer same-day service and can often be on site at short notice. Running out of time before a move-out or tenant changeover? Tell us your deadline when booking and we'll prioritise the job. You can book with 30 minutes' notice or plan up to 30 days ahead.",
        },
        {
          question: "Can you clear an apartment ahead of a renovation?",
          answer:
            "Yes, we regularly clear apartments before renovations, remodelling or tenant changeovers. We remove all furnishings, appliances and waste so the contractors can start right away. Tell us your deadline when booking and we will prioritise accordingly.",
        },
        {
          question: "Can you clear an apartment without a lift?",
          answer:
            "Yes, our workers are used to carrying furniture and belongings down stairs, even in older buildings without a lift. We scale the team size to match the floor level and volume so the apartment clearance runs smoothly and efficiently no matter how high up you live.",
        },
        {
          question: "Do you sort and donate usable items during apartment clearance?",
          answer:
            "Yes, we sort everything during the clearance. Furniture, clothes and household items in good condition are donated to organisations like Stadsmissionen and Myrorna. Electronics go to approved e-waste collection and the rest is taken to a recycling station. You may also be eligible for the Swedish RUT tax deduction when items are transported for reuse.",
        },
        {
          question: "Which areas do you offer apartment clearance in?",
          answer:
            "We offer apartment clearance throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,

      pricingRows: getPackagePricingRows("bortforsling", "en"),

      dynamicHeadings: {
        threeSteps: "Clear your apartment in 3 easy steps",
        step2Text:
          "Our workers move systematically through every room – disassembling furniture, disconnecting appliances and carrying everything out. Stairwells and floors are protected throughout.",
        ctaHeading: "Book apartment clearance today",
        ctaText:
          "Enter the address, describe the apartment and pick a time. You see the price instantly – give us a full apartment, we give it back empty.",
      },
    },
  },

  {
    // Tömma dödsbo — config för /tomma-dodsbo
    // Vinkel: Helhetsprocessen att tömma ett dödsbo — planering, sortering av
    // värdesaker/dokument, samordning med mäklare/BRF, stöd till anhöriga på
    // distans. INTE samma som bortforsling-dödsbo som fokuserar på transporten.
    slug: "tomma-dodsbo",
    serviceCategory: "tömningar",

    meta: {
      title: "Tömma dödsbo – Varsam & Professionell Dödsbotömning | Ungdomskraft",
      description:
        "Boka dödsbotömning i Stockholm och Göteborg. Vi hjälper med sortering, donation och ansvarsfull återvinning – varsamt och med fast pris. Boka idag.",
      h1: "Tömma dödsbo",
    },

    hero: {
      heading: "Hjälp att tömma dödsbo",
      intro:
        "Att tömma ett dödsbo berör på ett sätt som få andra uppgifter gör. Vi tar hand om hela processen åt dig — från noggrann genomgång av värdesaker och personliga dokument till tömning, donation och återvinning. Du bestämmer takten och vad som ska sparas. Vi sköter resten med omtanke.",
      heroImage: "/hero-bortforsling.webp",
    },

    featureCards: {
      headingText: "Vi sköter din dödsbotömning",
      sinceText: "Dödsbotömning sedan 2014",
      bookYourText: "Boka din dödsbotömning",
      bookDescText:
        "Ange bostadens adress, beskriv situationen och välj en tid som passar. Du får ett pris direkt — utan offertförfrågan.",
      vehicleTitleText: "Anpassat fordon för din dödsbotömning",
      vehicleDescText:
        "Vi matchar fordon och bemanning efter bostadens storlek. Från skåpbil för en etta till lastbil med bakgavellyft för villor med vind, källare och garage.",
    },

    itemsSection: {
      heading: "Vi tar hand om allt i dödsboet",
      items: [
        {
          name: "Möbler & bohag",
          description: "Soffor, sängar, skåp, bord och allt löst i hemmet",
          image: "/sofa.webp",
        },
        {
          name: "Kläder & textilier",
          description: "Garderober med kläder, sänglinne, handdukar och mattor",
          image: "/bedding.webp",
        },
        {
          name: "Husgeråd & porslin",
          description: "Kök, serviser, bestick, glas och prydnadsföremål",
          image: "/dishes.webp",
        },
        {
          name: "Vitvaror",
          description: "Kylskåp, spis, tvättmaskin, diskmaskin och frys",
          image: "/fridge.webp",
        },
        {
          name: "Dokument & foton",
          description: "Vi lägger åt sidan allt som ser personligt eller viktigt ut",
          image: "/box.webp",
        },
        {
          name: "Övrigt",
          description: "Böcker, verktyg, trädgårdssaker, elektronik och allt annat",
          image: "/tv.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Dödsbotömning med omtanke — från start till mål",
      mainText:
        "Ett dödsbo är mer än möbler och saker. Det är ett hem som berättar en historia — och att tömma det kräver både fingertoppskänsla och praktisk erfarenhet. Många anhöriga bor på annan ort, har ont om tid eller orkar helt enkelt inte hantera allt själva. Samtidigt tickar klockan: hyreskontrakt ska sägas upp, bostaden ska säljas och bouppteckningen behöver slutföras.\n\nMed Ungdomskraft får du en partner som tar ansvar för hela kedjan. Vi börjar med att gå igenom bostaden — antingen tillsammans med dig på plats eller via bilder om du bor långt bort. Personliga värdesaker, dokument, fotografier och smycken lägger vi varsamt åt sidan. Bohag i gott skick sorteras för donation till organisationer som Myrorna och Stadsmissionen. Resten hanteras ansvarsfullt via godkänd återvinningsstation.\n\nNär vi är klara lämnar vi bostaden tom, sopfri och redo för nästa steg — oavsett om det är en mäklarvisning, renovering eller nyckelöverlämning till hyresvärden.",
      preparationHeading: "Innan vi börjar — så kan du förbereda dig",
      preparationText:
        "Fundera på om det finns saker du eller andra familjemedlemmar vill behålla — smycken, fotoalbum, brev, konst eller specifika möbler. Plocka undan det du kan, eller märk det tydligt så vi vet. Resten behöver du inte röra.\n\nSamla gärna nycklar till alla utrymmen som hör till bostaden — vind, källare, förråd och garage. Om du inte kan vara på plats, kan du lämna nycklar hos en granne, mäklare eller fastighetsskötare. Berätta om det finns deadlines, till exempel uppsagt hyreskontrakt eller planerad visning, så prioriterar vi därefter.",
      additionalSections: [
        {
          heading: "Vad skiljer dödsbotömning från vanlig bortforsling?",
          content:
            "Vanlig bortforsling handlar om att köra iväg saker du redan bestämt dig för att bli av med. En dödsbotömning är en helt annan process:",
          listItems: [
            {
              label: "Sortering av värdesaker –",
              text: "vi går igenom lådor, garderober och hyllor för att hitta och lägga åt sidan saker som kan ha ekonomiskt eller sentimentalt värde — foton, brev, smycken, kontanter och dokument",
            },
            {
              label: "Samordning med anhöriga –",
              text: "vi arbetar ofta med familjemedlemmar som bor på annan ort. Du kan styra hela processen via telefon eller bilder utan att behöva vara på plats",
            },
            {
              label: "Anpassning efter tidsplan –",
              text: "dödsbon har ofta hårda deadlines — uppsagt hyresavtal, bouppteckning eller planerad visning. Vi anpassar schemat efter dina behov, inte tvärtom",
            },
            {
              label: "Helhetslösning –",
              text: "vi tömmer inte bara bostaden utan även vind, källare, förråd och garage i samma uppdrag. Allt ingår i en bokning",
            },
          ],
        },
        {
          heading: "Så genomför vi en dödsbotömning",
          listItems: [
            {
              label: "1. Kontakt och planering –",
              text: "Du beskriver bostaden — storlek, antal rum, eventuella extra utrymmen. Vi ger dig ett fast pris. Har du bilder? Skicka gärna, det hjälper oss planera rätt bemanning och fordon.",
            },
            {
              label: "2. Genomgång av värdesaker –",
              text: "Innan vi börjar tömma går vi (eller du) igenom bostaden. Personliga saker, dokument och föremål av värde lägger vi åt sidan i märkta kartonger. Du bestämmer vad som händer med dem.",
            },
            {
              label: "3. Tömning rum för rum –",
              text: "Våra ungdomar arbetar systematiskt genom hela bostaden. Möbler monteras isär, vitvaror kopplas loss och allt bärs ut och lastas. Golv och trapphus skyddas.",
            },
            {
              label: "4. Donation och återvinning –",
              text: "Kläder, möbler och husgeråd i bra skick skänks vidare. Elektronik lämnas till e-avfall. Övrigt sorteras på godkänd återvinningsstation. Bostaden lämnas tom och sopfri.",
            },
          ],
        },
        {
          heading: "Vi stöttar dig — även på distans",
          content:
            "Det är vanligt att anhöriga bor i en helt annan stad — ibland till och med utomlands. Vi är vana vid att sköta dödsbotömningar på distans. Du bokar online, skickar bilder på bostaden och lämnar nycklar hos en kontaktperson. Under tömningen skickar vi uppdateringar och bilder så att du kan följa arbetet.\n\nHittar vi saker som verkar personliga eller värdefulla — fotoalbum, brev, smycken, kontanter — kontaktar vi dig direkt innan vi går vidare. Ingenting kastas utan din bekräftelse.\n\nNär vi är klara skickar vi en sammanfattning av vad som skänkts, återvunnits och deponerats. Du behöver aldrig oroa dig för att något viktigt försvinner.",
        },
        {
          heading: "Vanliga situationer vid dödsbotömning",
          listItems: [
            {
              label: "Hyreskontrakt som löper ut –",
              text: "hyresvärden kräver att lägenheten tömms inom en viss tid. Vi anpassar oss efter din deadline, ofta med bara några dagars varsel.",
            },
            {
              label: "Bostadsrätt som ska säljas –",
              text: "mäklaren behöver en tom bostad inför fotografering och visning. Vi tömmer och lämnar bostaden redo för försäljning.",
            },
            {
              label: "Villa med många utrymmen –",
              text: "huset har vind, källare, garage och trädgårdsbod som alla behöver tömmas. Vi hanterar allt i ett och samma uppdrag.",
            },
            {
              label: "Familjemedlemmar på olika orter –",
              text: "alla vill ha möjlighet att välja ut saker. Vi kan genomföra tömningen i etapper eller lägga undan saker för hämtning.",
            },
          ],
        },
        {
          heading: "Boka dödsbotömning idag",
          content:
            "Vi vet att det inte alltid är lätt att ta det första steget. Men när du väl bestämt dig gör vi processen så enkel som möjligt. Du berättar om bostaden, vi ger dig ett fast pris, och våra ungdomar tar hand om allt — varsamt, noggrant och med respekt.\n\nFyll i formuläret och se priset direkt, eller ring oss på 08-517 082 81 så pratar vi igenom ditt ärende.",
          cta: { text: "Boka dödsbotömning", href: "#priser" },
        },
      ],
    },

    faq: [
      {
        question: "Hur skiljer sig dödsbotömning från vanlig bortforsling av dödsbo?",
        answer:
          "Bortforsling av dödsbo fokuserar på att köra iväg saker du redan sorterat och bestämt dig för att bli av med. Dödsbotömning är en helhetstjänst där vi tar hand om hela processen — genomgång av värdesaker och dokument, sortering, donation, tömning och återvinning. Du behöver inte förbereda eller sortera något i förväg.",
      },
      {
        question: "Vad kostar det att tömma ett dödsbo?",
        answer:
          "Priset beror på bostadens storlek, mängden bohag och eventuella extra utrymmen som vind, källare och förråd. En etta kostar oftast 3 000–5 000 kr. En trea med källare och vind kan landa på 8 000–15 000 kr. Du får alltid ett fast pris vid bokning — inga dolda avgifter. Avfallsavgifter tillkommer separat.",
      },
      {
        question: "Hur hanterar ni personliga värdesaker och dokument?",
        answer:
          "Vi går igenom bostaden noggrant och lägger åt sidan allt som kan ha personligt eller ekonomiskt värde — foton, brev, smycken, kontanter, juridiska dokument och konst. Sakerna placeras i märkta kartonger som du hämtar eller får hemlevererade. Ingenting kastas utan din bekräftelse.",
      },
      {
        question: "Kan ni tömma dödsboet om jag bor på annan ort?",
        answer:
          "Ja, det är mycket vanligt. Du bokar online, skickar bilder på bostaden och lämnar nycklar hos en granne, mäklare eller fastighetsskötare. Vi skickar uppdateringar och bilder under arbetets gång. Hittar vi saker som kan vara värdefulla kontaktar vi dig innan vi går vidare.",
      },
      {
        question: "Hur lång tid tar en dödsbotömning?",
        answer:
          "En etta tar vanligtvis en halv till en hel dag. En trea tar en till två dagar. Större villor med vind, källare och garage kan ta två till tre dagar. Vi anpassar bemanningen efter bostadens storlek för att det ska gå så smidigt som möjligt.",
      },
      {
        question: "Tömmer ni även vind, källare, förråd och garage?",
        answer:
          "Ja, allt ingår i samma uppdrag. Vi tömmer hela bostaden och alla tillhörande utrymmen. Ange vid bokning vilka utrymmen som ska tömmas, så planerar vi fordon och bemanning därefter.",
      },
      {
        question: "Kan ni samordna med mäklare eller hyresvärd?",
        answer:
          "Absolut. Vi är vana vid att koordinera med mäklare, hyresvärdar och bostadsrättsföreningar. Behöver lägenheten vara tom till ett visst datum? Ska mäklaren komma in för fotografering efteråt? Berätta det vid bokning så planerar vi arbetet därefter.",
      },
      {
        question: "Vad händer med bohaget efter tömningen?",
        answer:
          "Möbler, kläder och husgeråd i bra skick skänks till organisationer som Myrorna och Stadsmissionen. Elektronik lämnas till godkänd e-avfallsmottagning. Resten sorteras och körs till återvinningsstation. Vi rapporterar vad som skänkts, återvunnits och deponerats.",
      },
      {
        question: "Kan jag få RUT-avdrag på dödsbotömning?",
        answer:
          "Om bohaget transporteras till en secondhand-butik eller organisation för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Avdraget hanteras direkt på fakturan, så du behöver aldrig ligga ute med pengarna. Vanlig tömning där allt körs direkt till återvinning ger inte rätt till avdrag.",
      },
      {
        question: "Kan ni genomföra tömningen i etapper?",
        answer:
          "Ja, vi kan tömma i omgångar om familjemedlemmar vill ha tid att gå igenom saker mellan tillfällena. Vi planerar helt efter dina behov — vissa föredrar att vi tömmer allt på en dag, andra vill ha två eller tre besök med tid emellan.",
      },
      {
        question: "Ingår avfallsavgifter i priset?",
        answer:
          "Nej, avfalls- och deponiavgifter debiteras separat. Vid en dödsbotömning brukar avgiften ligga på 500–1 500 kr beroende på mängd och typ av avfall. Du får alltid en uppskattning innan vi börjar.",
      },
      {
        question: "Hur snart efter dödsfallet kan ni påbörja dödsbotömningen?",
        answer:
          "Vi kan vara på plats redan samma vecka som ni kontaktar oss. Många familjer behöver tid att gå igenom personliga tillhörigheter först – vi anpassar oss helt efter er tidsplan. Har ni en pressad deadline, exempelvis ett hyresavtal som löper ut, prioriterar vi uppdraget.",
      },
      {
        question: "Hanterar ni ömtåliga föremål som antikviteter och konstverk vid dödsbotömning?",
        answer:
          "Ja, våra ungdomar har erfarenhet av att hantera tunga och ömtåliga föremål varsamt. Antikviteter, konstverk och värdefulla möbler skyddas under transport. Om ni vill att särskilda föremål värderas eller säljs kan vi rekommendera samarbetspartners som hjälper till med det.",
      },
      {
        question: "Vilka områden erbjuder ni dödsbotömning i?",
        answer:
          "Vi erbjuder dödsbotömning i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Tömma dödsbo i 3 enkla steg",
      step2Text:
        "Våra ungdomar går igenom bostaden rum för rum. Värdesaker läggs åt sidan, möbler monteras isär, vitvaror kopplas loss och allt bärs ut. Du bestämmer vad som sparas.",
      ctaHeading: "Boka dödsbotömning idag",
      ctaText:
        "Fyll i adressen, beskriv bostaden och välj en tid. Du ser priset direkt — vi tar hand om resten med omtanke.",
    },

    en: {
      slug: "house-clearance",

      featureCards: {
        sinceText: "House clearance since 2014",
        bookYourText: "Book your house clearance",
        bookDescText:
          "Enter the property address, describe the situation and pick a time that suits you. You get an instant price — no quote request needed.",
        vehicleTitleText: "The right vehicle for your house clearance",
        vehicleDescText:
          "We match the vehicle and crew to the property's size. From a van for a studio to a truck with tail lift for houses with attic, basement and garage.",
      },

      meta: {
        title: "House Clearance – Careful & Professional | Ungdomskraft",
        description:
          "Book house clearance with care. We help you through the entire process – sorting valuables, donating furnishings and responsible recycling. Fixed price.",
        h1: "House clearance",
      },

      hero: {
        heading: "Help with house clearance",
        intro:
          "Clearing an estate is unlike any other task. We handle the entire process for you — from careful review of valuables and personal documents to clearance, donation and recycling. You set the pace and decide what to keep. We take care of the rest with compassion.",
      },

      itemsSection: {
        heading: "We take care of everything in the estate",
        items: [
          {
            name: "Furniture & furnishings",
            description: "Sofas, beds, cabinets, tables and all loose items",
            image: "/sofa.webp",
          },
          {
            name: "Clothes & textiles",
            description: "Wardrobes of clothes, bed linen, towels and rugs",
            image: "/bedding.webp",
          },
          {
            name: "Kitchenware & china",
            description: "Kitchen items, dinner sets, cutlery, glassware and ornaments",
            image: "/dishes.webp",
          },
          {
            name: "Appliances",
            description: "Fridge, stove, washing machine, dishwasher and freezer",
            image: "/fridge.webp",
          },
          {
            name: "Documents & photos",
            description: "We set aside anything that looks personal or important",
            image: "/box.webp",
          },
          {
            name: "Everything else",
            description: "Books, tools, garden items, electronics and all the rest",
            image: "/tv.webp",
          },
        ],
      },

      seoContent: {
        mainHeading: "House clearance with care — from start to finish",
        mainText:
          "An estate is more than furniture and possessions. It's a home that tells a story — and clearing it requires both sensitivity and practical experience. Many family members live far away, are short on time or simply can't face handling everything alone. Meanwhile the clock is ticking: the lease needs cancelling, the property needs selling and probate needs completing.\n\nWith Ungdomskraft you get a partner who takes responsibility for the entire chain. We start by going through the property — either with you on site or via photos if you live far away. Personal valuables, documents, photographs and jewellery are carefully set aside. Furnishings in good condition are sorted for donation to charity organisations. The rest is handled responsibly at approved recycling stations.\n\nWhen we're done, the property is left empty, clean and ready for whatever comes next — whether that's a viewing, renovation or handing the keys back to the landlord.",
        preparationHeading: "Before we begin — how to prepare",
        preparationText:
          "Think about whether there are items you or other family members want to keep — jewellery, photo albums, letters, artwork or specific furniture. Set aside what you can, or mark it clearly so we know. You don't need to touch the rest.\n\nGather keys to all spaces that belong to the property — attic, basement, storage and garage. If you can't be present, you can leave keys with a neighbour, estate agent or building manager. Let us know about any deadlines, such as a lease ending or a planned viewing, so we can prioritise accordingly.",
        additionalSections: [
          {
            heading: "What makes house clearance different from standard removal?",
            content:
              "Standard junk removal is about hauling away items you've already decided to get rid of. House clearance is a completely different process:",
            listItems: [
              {
                label: "Sorting valuables –",
                text: "we go through drawers, wardrobes and shelves to find and set aside anything of financial or sentimental value — photos, letters, jewellery, cash and documents",
              },
              {
                label: "Coordinating with family –",
                text: "we often work with family members who live in a different city or even abroad. You can manage the entire process by phone or photos without being on site",
              },
              {
                label: "Working to your timeline –",
                text: "estates often come with hard deadlines — an expiring lease, probate or a planned property viewing. We adapt to your schedule, not the other way around",
              },
              {
                label: "Complete solution –",
                text: "we don't just clear the main rooms — attic, basement, storage and garage are all included in a single booking",
              },
            ],
          },
          {
            heading: "How we carry out a house clearance",
            listItems: [
              {
                label: "1. Contact and planning –",
                text: "You describe the property — size, number of rooms, any extra spaces. We give you a fixed price. Have photos? Send them along — it helps us plan the right crew and vehicle.",
              },
              {
                label: "2. Valuables review –",
                text: "Before we start clearing, we (or you) go through the property. Personal items, documents and valuables are set aside in labelled boxes. You decide what happens to them.",
              },
              {
                label: "3. Room-by-room clearance –",
                text: "Our workers move systematically through the home. Furniture is disassembled, appliances disconnected and everything is carried out and loaded. Floors and stairwells are protected.",
              },
              {
                label: "4. Donation and recycling –",
                text: "Clothes, furniture and kitchenware in good condition are donated to charity. Electronics go to e-waste. The rest is sorted at an approved recycling station. The property is left empty and clean.",
              },
            ],
          },
          {
            heading: "We support you — even from a distance",
            content:
              "It's common for family members to live in a completely different city — sometimes even abroad. We're experienced in handling estate clearances remotely. You book online, send photos of the property and leave keys with a contact person. During the clearance we send updates and photos so you can follow the progress.\n\nIf we find items that look personal or valuable — photo albums, letters, jewellery, cash — we contact you directly before moving on. Nothing is discarded without your confirmation.\n\nWhen we're done, we send a summary of what was donated, recycled and disposed of. You never have to worry about something important going missing.",
          },
          {
            heading: "Common situations for house clearance",
            listItems: [
              {
                label: "Lease expiring –",
                text: "the landlord requires the apartment to be emptied within a set timeframe. We adapt to your deadline, often at just a few days' notice.",
              },
              {
                label: "Property for sale –",
                text: "the estate agent needs an empty property for photography and viewings. We clear it and leave it ready for market.",
              },
              {
                label: "House with multiple spaces –",
                text: "the property has an attic, basement, garage and garden shed that all need clearing. We handle everything in a single job.",
              },
              {
                label: "Family members in different locations –",
                text: "everyone wants the chance to choose keepsakes. We can carry out the clearance in stages or set items aside for collection.",
              },
            ],
          },
          {
            heading: "Book house clearance today",
            content:
              "We know taking the first step isn't always easy. But once you've decided, we make the process as smooth as possible. You tell us about the property, we give you a fixed price, and our workers take care of everything — carefully, thoroughly and with respect.\n\nFill in the form and see the price instantly, or call us at +46 8-517 082 81 to talk through your situation.",
            cta: { text: "Book house clearance", href: "#priser" },
          },
        ],
      },

      faq: [
        {
          question: "How is house clearance different from standard junk removal?",
          answer:
            "Standard junk removal focuses on hauling away items you've already sorted and decided to discard. House clearance is a full-service solution where we handle the entire process — reviewing valuables and documents, sorting, donating, clearing and recycling. You don't need to prepare or sort anything in advance.",
        },
        {
          question: "How much does house clearance cost?",
          answer:
            "The price depends on the property's size, the amount of furnishings and any extra spaces like attic, basement and storage. A studio usually costs SEK 3,000–5,000. A three-bedroom home with basement and attic can come to SEK 8,000–15,000. You always get a fixed price when booking — no hidden fees. Waste disposal fees are charged separately.",
        },
        {
          question: "How do you handle personal valuables and documents?",
          answer:
            "We go through the property carefully and set aside everything that may have personal or financial value — photos, letters, jewellery, cash, legal documents and artwork. Items are placed in labelled boxes for you to collect or have delivered. Nothing is discarded without your confirmation.",
        },
        {
          question: "Can you clear the estate if I live in a different city?",
          answer:
            "Yes, this is very common. You book online, send photos of the property and leave keys with a neighbour, estate agent or building manager. We send updates and photos during the work. If we find items that may be valuable, we contact you before proceeding.",
        },
        {
          question: "How long does a house clearance take?",
          answer:
            "A studio usually takes half a day to a full day. A three-bedroom home takes one to two days. Larger houses with attic, basement and garage can take two to three days. We scale the crew to the property's size to keep things moving smoothly.",
        },
        {
          question: "Do you clear the attic, basement, storage and garage too?",
          answer:
            "Yes, everything is included in the same booking. We clear the entire property and all associated spaces. Just let us know which spaces need clearing when you book, and we'll plan the right vehicle and crew.",
        },
        {
          question: "Can you coordinate with an estate agent or landlord?",
          answer:
            "Absolutely. We're used to working alongside estate agents, landlords and housing associations. Need the property empty by a certain date? Is the agent coming in for photography afterwards? Tell us when booking and we'll plan the work accordingly.",
        },
        {
          question: "What happens to the furnishings after the clearance?",
          answer:
            "Furniture, clothes and kitchenware in good condition are donated to charity organisations. Electronics go to approved e-waste collection. The rest is sorted and taken to a recycling station. We provide a summary of what was donated, recycled and disposed of.",
        },
        {
          question: "Am I eligible for a RUT tax deduction on house clearance?",
          answer:
            "If the furnishings are transported to a charity shop or organisation for reuse, you're eligible for the RUT deduction on the labour cost. The deduction is applied directly on the invoice, so you never have to pay upfront. A standard clearance where everything goes straight to recycling does not qualify.",
        },
        {
          question: "Can the clearance be done in stages?",
          answer:
            "Yes, we can clear in rounds if family members need time to go through items between visits. We plan entirely around your needs — some prefer everything done in a single day, others want two or three visits with time in between.",
        },
        {
          question: "Are waste disposal fees included in the price?",
          answer:
            "No, waste and landfill fees are charged separately. For a house clearance, fees usually run SEK 500–1,500 depending on the volume and type of waste. You always get an estimate before we start.",
        },
        {
          question: "How soon after the death can you start the house clearance?",
          answer:
            "We can be on site as early as the same week you contact us. Many families need time to go through personal belongings first – we adapt entirely to your schedule. If you have a tight deadline, for example a tenancy agreement running out, we will prioritise the job.",
        },
        {
          question: "Do you handle delicate items like antiques and artwork during estate clearance?",
          answer:
            "Yes, our workers have experience handling heavy and delicate items with care. Antiques, artwork and valuable furniture are protected during transport. If you want specific items valued or sold, we can recommend partners who can assist with that.",
        },
        {
          question: "Which areas do you offer house clearance in?",
          answer:
            "We offer house clearance throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,

      pricingRows: getPackagePricingRows("bortforsling", "en"),

      dynamicHeadings: {
        threeSteps: "House clearance in 3 easy steps",
        step2Text:
          "Our workers go through the home room by room. Valuables are set aside, furniture is disassembled, appliances disconnected and everything is carried out. You decide what's kept.",
        ctaHeading: "Book house clearance today",
        ctaText:
          "Enter the address, describe the property and pick a time. You see the price instantly — we take care of the rest with compassion.",
      },
    },
  },

  {
    // Tömma kontor — config för /tomma-kontor
    // Vinkel: Komplett kontorstömning — kontorsmöbler, IT-utrustning, arkiv och sekretesshantering.
    // Unik vinkel vs bortforsling: fokus på företag, GDPR-hantering, helg-/kvällsjobb, hyresavtal.
    slug: "tomma-kontor",
    serviceCategory: "tömningar",

    meta: {
      title: "Tömma kontor – Snabb & Smidig Kontorstömning | Ungdomskraft",
      description:
        "Boka kontorstömning snabbt och enkelt. Vi tömmer hela kontoret – möbler, IT-utrustning och arkiv. Fast pris, flexibla tider – även kvällar och helger.",
      h1: "Tömma kontor",
    },

    hero: {
      heading: "Hjälp att tömma kontor",
      intro:
        "Ska ni flytta, säga upp lokalen eller bara rensa ut? Vi tömmer hela kontoret åt er – skrivbord, kontorsstolar, skåp, IT-utrustning och arkivmaterial. Våra ungdomar arbetar snabbt och strukturerat, och vi kan jobba kvällar och helger så att verksamheten inte störs. Allt sorteras och körs till elektronikåtervinning, möbelåterbruk eller deponi.",
      heroImage: "/hero-bortforsling.webp",
    },

    featureCards: {
      headingText: "Vi sköter din kontorstömning",
      sinceText: "Kontorstömning sedan 2014",
      bookYourText: "Boka din kontorstömning",
      bookDescText:
        "Ange adressen till kontoret, beskriv vad som ska bort och boka en tid som passar – gärna kväll eller helg. Du ser priset direkt.",
      vehicleTitleText: "Anpassat fordon för din kontorstömning",
      vehicleDescText:
        "Vi har flera fordon i olika storlekar. Vi väljer det fordon som passar uppdraget bäst – från ett enstaka rum till ett helt kontorsplan.",
    },

    itemsSection: {
      heading: "Vanligt i kontor vi tömmer",
      items: [
        {
          name: "Kontorsmöbler",
          description: "Skrivbord, stolar, soffor, arbetsstationer och övriga kontorsmöbler",
          image: "/office-desk.webp",
        },
        {
          name: "Elektronik",
          description: "Datorer, skärmar, kopiatorer, skrivare och annan kontorselektronik",
          image: "/copy-machine.webp",
        },
        {
          name: "Småapparater",
          description: "Mikrovågsugnar, minikylar, brödrostar, kaffemaskiner och andra småapparater",
          image: "/mini-fridge.webp",
        },
        {
          name: "Växter",
          description: "Inomhus- och utomhusväxter, blommor och krukträd",
          image: "/plant.webp",
        },
        {
          name: "Inredning",
          description: "Konst, tavlor och andra dekorativa föremål",
          image: "/painting.webp",
        },
        {
          name: "Lådor & kartonger",
          description: "Kontorsmaterial, böcker, dokument och övriga företagstillhörigheter",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Professionell kontorstömning – snabbt, smidigt och hållbart",
      mainText:
        "När ett hyresavtal löper ut, verksamheten flyttar eller kontoret ska renoveras behöver allt bort – och det behöver gå fort. Ungdomskraft erbjuder kontorstömning som passar företagets tidsplan: vi jobbar kvällar, helger och nätter så att ni inte tappar en enda arbetsdag. Vi tar hand om allt från skrivbord och kontorsstolar till servrar, arkivskåp och personalkökets vitvaror. Elektronik lämnas till certifierad elektronikåtervinning, möbler som fortfarande fungerar skickas till återbruk och resten sorteras för deponi. Ni behöver inte stå för container, transport eller tippavgifter – vi hanterar hela kedjan. Oavsett om det handlar om ett litet kontor med fem arbetsplatser eller ett helt kontorslandskap med hundratals kvadratmeter, anpassar vi oss helt efter era behov. Vi börjar alltid med en inventering – antingen på plats eller via foton ni skickar – och ger en fast offert innan arbetet sätter igång. Det innebär inga dolda kostnader och inga överraskningar på fakturan.",
      preparationHeading: "Förbered kontorstömningen",
      preparationText:
        "Gå igenom kontoret rum för rum och bestäm vad som ska sparas, säljas eller kastas. Samla ihop konfidentiella dokument och hårddiskar separat – vi kan köra dem direkt till certifierad destruktion. Meddela fastighetsägaren och stäm av datum med vår kundtjänst. Om ni har leasade kopiatorer eller kaffeautomater, kontakta leverantören först så att de hämtas innan vi börjar. Tänk också på att inventera personalköket: kaffemaskiner, kylskåp och mikrovågsugnar glöms ofta bort men tar tid att hantera i sista sekund. Kontrollera om det finns kemikalier, batterier eller andra specialavfall som behöver separat hantering. Ju mer som är förberett, desto snabbare och billigare blir tömningen.",
      additionalSections: [
        {
          heading: "GDPR och sekretess vid kontorstömning",
          content:
            "Många kontor har känsligt material – kundregister, personalakter, affärsavtal och gamla hårddiskar. Vi hanterar sekretessmaterial med stor försiktighet. Dokument kan samlas i separata säckar och lämnas till certifierad dokumentförstöring. Hårddiskar och USB-minnen sorteras ut och skickas till elektronikåtervinning med spårbar destruktion. Ni får kvitto på att materialet hanterats enligt gällande regler, vilket gör det enkelt att dokumentera för GDPR-revisioner. Vi kan även hantera servrar, NAS-enheter och backup-band – allt som kan innehålla personuppgifter eller företagshemligheter. Säkerhet och spårbarhet är grundpelare i vårt arbetssätt, och vi anpassar oss efter er interna policy om ni har specifika rutiner för datahantering.",
        },
        {
          heading: "Kvällar, helger och expresstömning",
          content:
            "Företag har sällan tid att stänga verksamheten mitt i veckan. Därför erbjuder vi kontorstömning på tider som passar er: tidiga morgnar, sena kvällar, helger eller till och med nattetid för större projekt. Behöver ni tömma kontoret innan helgen? Vi har kapacitet för expresstömning med extra team – ofta ner till 24 timmars förvarning. Berätta om er deadline så anpassar vi planeringen. Många av våra företagskunder uppskattar att arbetet sker helt utanför kontorstid – medarbetarna går hem på fredagen och kommer tillbaka på måndag morgon till ett tomt, städat kontor. Vi samordnar gärna med er städfirma så att lokalen är inflyttningsklar för nästa hyresgäst.",
        },
        {
          heading: "Från enstaka rum till hela kontorsplan",
          listItems: [
            {
              label: "Enstaka rum",
              text: "Ett mötesrum, ett förråd eller en chefssvit – vi hämtar allt på en enda tur.",
            },
            {
              label: "Våningsplan",
              text: "Öppen planlösning med 20–50 arbetsplatser? Vi monterar ner, bär ut och kör bort på en kväll.",
            },
            {
              label: "Hela kontoret",
              text: "Flera plan, reception, personalmatsal och serverrum – vi projektleder tömningen från start till nyckelöverlämning.",
            },
            {
              label: "Co-working & pop-up",
              text: "Snabb in- och utrensning av kontorsplatser vid byte av hyresgäst eller ombyggnad.",
            },
          ],
          afterContent:
            "Oavsett storlek får ni en fast offert innan vi börjar, så det finns inga överraskningar på fakturan.",
        },
        {
          heading: "Varför boka kontorstömning med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet. När du bokar hos oss får du inte bara professionell hjälp – du bidrar också till att unga människor kommer ut i arbetslivet och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som får lära sig allt från kundkontakt och logistik till fysiskt arbete och ansvarsfullt miljöarbete. Sedan starten 2014 har vi hjälpt hundratals företag att tömma sina kontor, och under resans gång har vi gett tusentals unga deras första riktiga arbetslivserfarenhet. Det innebär att varje kontorstömning ni bokar är mer än bara en tjänst – det är en investering i nästa generation. Våra medarbetare får praktisk träning i att arbeta i team, kommunicera med kunder, planera logistik och ta ansvar för tunga lyft och sortering. De lär sig vikten av att komma i tid, vara noggrann och leverera ett resultat som kunden är nöjd med. Det här syns i kvaliteten på jobbet: våra team är engagerade, energiska och angelägna om att göra sitt bästa. Vi har byggt en kultur där varje uppdrag ses som en möjlighet att bevisa sig, och det märks i hur noggrant och effektivt arbetet utförs. Ni som företag får dessutom en partner som delar era värderingar kring hållbarhet och socialt ansvar. Allt material vi hämtar sorteras för maximal återvinning – möbler som fortfarande är i gott skick skickas till återbruk, elektronik till certifierad e-avfallshantering och övrigt avfall till godkänd deponi. Vi dokumenterar hanteringen så att ni kan använda det i ert hållbarhetsarbete och CSR-rapportering. Att välja Ungdomskraft för er kontorstömning innebär att ni får ett snabbt, pålitligt och prisvärt jobb utfört – samtidigt som ni stöttar ungdomar på vägen in i arbetslivet och bidrar till en mer hållbar hantering av kontorsavfall. Det är professionell hjälp med mening.",
        },
      ],
    },

    faq: [
      {
        question: "Kan ni tömma kontoret på kvällar eller helger?",
        answer:
          "Ja, vi erbjuder kontorstömning kvällar, helger och vid behov nattetid. Många företag föredrar att vi jobbar utanför kontorstid för att inte störa verksamheten. Ange önskad tid vid bokning så anpassar vi oss.",
      },
      {
        question: "Hur hanterar ni sekretessmaterial och hårddiskar?",
        answer:
          "Konfidentiella dokument samlas separat och lämnas till certifierad dokumentförstöring. Hårddiskar, USB-minnen och annan datalagring sorteras ut och skickas till elektronikåtervinning med spårbar destruktion. Ni får kvitto på att materialet hanterats korrekt.",
      },
      {
        question: "Ingår demontering av möbler i priset?",
        answer:
          "Ja, vi demonterar skrivbord, hyllsystem och skåp som en del av tömningen. Ni behöver inte förbereda något – vi har verktyg och erfarenhet av alla vanliga kontorsmöbler från IKEA, Kinnarps, AJ Produkter med flera.",
      },
      {
        question: "Vad händer med IT-utrustningen?",
        answer:
          "Funktionsduglig elektronik skickas till återbruk och certifierad dataradering. Trasig utrustning lämnas till el-avfall. Vi sorterar på plats så att ni inte behöver fundera på vad som ska vart.",
      },
      {
        question: "Kan ni fakturera företaget?",
        answer:
          "Ja, vi fakturerar med 30 dagars betalningsvillkor. Ange organisationsnummer och faktureringsadress vid bokning. Moms specificeras på fakturan.",
      },
      {
        question: "Hur snabbt kan ni tömma ett helt kontor?",
        answer:
          "Ett mindre kontor (5–10 arbetsplatser) klarar vi normalt på en kväll. Större kontor med 30+ platser tar oftast en till två dagar. Vi kan sätta in extra team om ni har en pressad deadline – kontakta oss för en tidsplan.",
      },
      {
        question: "Vad kostar det att tömma ett kontor?",
        answer:
          "Priset beror på mängden möbler, elektronik och skräp. Ett litet kontor med 5–10 arbetsplatser landar ofta på 3 000–6 000 kr. Större kontor offereras individuellt. Ni ser ett uppskattat pris direkt vid bokning och får en fast offert efter inventering.",
      },
      {
        question: "Kan ni tömma bara delar av ett kontor?",
        answer:
          "Ja, vi tömmer allt från enskilda mötesrum och kontorsrum till hela våningsplan. Behöver ni bara bli av med möblerna i ett rum medan resten står kvar? Inga problem – beskriv omfattningen vid bokning så anpassar vi teamet och priset efter ert behov.",
      },
      {
        question: "Hanterar ni kontorstömning för stora öppna kontorslandskap?",
        answer:
          "Absolut. Vi har erfarenhet av att tömma öppna kontorslandskap med 50+ arbetsplatser. Vi sätter in flera team parallellt och koordinerar logistiken med hissar, lastkajer och transportvägar för att minimera störningen. Kontakta oss för en skräddarsydd tidsplan.",
      },
      {
        question: "Hur säkerställer ni miljöriktig hantering av elektronik vid kontorstömning?",
        answer:
          "All elektronik – datorer, skärmar, skrivare och servrar – lämnas till certifierade WEEE-mottagare som hanterar elavfall enligt gällande regelverk. Vi dokumenterar hanteringen så att ni kan visa att ert företag uppfyllt sitt producentansvar. Hårddiskar kan destrueras på plats om ni önskar.",
      },
      {
        question: "Kan ni tömma kontorsarkiv och dokumentskåp?",
        answer:
          "Ja, vi tömmer arkivskåp, hyllsystem och dokumentskåp. Papper som inte innehåller känslig information körs till pappersåtervinning. Behöver ni säker dokumentförstöring kan vi koordinera med en certifierad partner som makulerar materialet åt er.",
      },
      {
        question: "Samordnar ni kontorstömningen med fastighetsägaren?",
        answer:
          "Ja, vi kan koordinera direkt med er fastighetsförvaltare eller hyresvärd kring tider, lastkajer och hissanvändning. Det gör kontorstömningen smidigare och säkerställer att fastighetens regler följs.",
      },
      {
        question: "Är kontorstömningen försäkrad?",
        answer:
          "Ja, alla våra uppdrag – inklusive kontorstömningar – är försäkrade genom Trygg-Hansa. Det innebär att er lokal, era tillhörigheter och våra medarbetare är skyddade under hela processen, från demontering till transport och bortforsling.",
      },
      {
        question: "I vilka kommuner erbjuder ni kontorstömning?",
        answer:
          "Vi erbjuder kontorstömning i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Kontorstömning i 3 enkla steg",
      step2Text:
        "Våra ungdomar går igenom kontoret systematiskt: möbler demonteras, elektronik sorteras, sekretessmaterial skiljs ut och allt bärs ner till vårt fordon. Ni bestämmer vad som ska sparas.",
      ctaHeading: "Boka kontorstömning idag",
      ctaText:
        "Ange kontorets adress, beskriv vad som ska bort och välj en tid som passar. Du ser priset direkt – vi sköter resten.",
    },

    en: {
      slug: "office-clearance",
      meta: {
        title: "Office Clearance – Fast & Affordable | Ungdomskraft",
        description:
          "Book office clearance in Stockholm & Gothenburg. We clear desks, IT equipment, filing cabinets and more. Fixed price – evenings and weekends available.",
        h1: "Office clearance",
      },
      hero: {
        heading: "Help with office clearance",
        intro:
          "Moving premises, ending a lease or just clearing out? We empty the entire office for you — desks, chairs, cabinets, IT equipment and archive material. Our workers are fast and methodical, and we can work evenings and weekends so your business keeps running. Everything is sorted and taken to electronics recycling, furniture reuse or disposal.",
      },
      featureCards: {
        sinceText: "Office clearance since 2014",
        bookYourText: "Book your office clearance",
        bookDescText:
          "Enter the office address, describe what needs to go and pick a time — evenings and weekends welcome. You see the price instantly.",
        vehicleTitleText: "Right vehicle for your office clearance",
        vehicleDescText:
          "We have several vehicles in different sizes. We pick the one that suits the job — from a single room to an entire office floor.",
      },
      itemsSection: {
        heading: "Common items in offices we clear",
        items: [
          {
            name: "Office furniture",
            description: "Desks, chairs, couches, workstations and other office furniture",
            image: "/office-desk.webp",
          },
          {
            name: "Electronics",
            description: "Computers, TVs, copiers, printers and other office electronics",
            image: "/copy-machine.webp",
          },
          {
            name: "Small appliances",
            description: "Mini fridges, microwaves, toasters, coffee machines and other small appliances",
            image: "/mini-fridge.webp",
          },
          {
            name: "House plants",
            description: "Indoor and outdoor plants, flowers and potted trees",
            image: "/plant.webp",
          },
          {
            name: "Decor",
            description: "Artwork and other decorative items",
            image: "/painting.webp",
          },
          {
            name: "Boxes",
            description: "Office supplies, books, documents and other company items",
            image: "/box.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Professional office clearance — fast, smooth and sustainable",
        mainText:
          "When a lease expires, the business relocates or the office needs a refurbishment, everything has to go — and fast. Ungdomskraft offers office clearance that fits your schedule: we work evenings, weekends and overnight so you don't lose a single working day. We handle everything from desks and office chairs to servers, filing cabinets and kitchen appliances. Electronics go to certified e-waste recycling, usable furniture is sent for reuse and the rest is sorted for disposal. You don't need to arrange a skip, transport or landfill fees — we manage the entire chain. Whether it's a small office with five workstations or a sprawling office landscape covering hundreds of square metres, we adapt entirely to your needs. We always start with an inventory — either on-site or from photos you send — and provide a fixed quote before work begins. That means no hidden costs and no surprises on the invoice.",
        preparationHeading: "Preparing for office clearance",
        preparationText:
          "Walk through the office room by room and decide what to keep, sell or discard. Gather confidential documents and hard drives separately — we can take them straight to certified destruction. Notify the landlord and confirm a date with our customer service. If you have leased copiers or coffee machines, contact the supplier first so they're collected before we start. Don't forget to check the staff kitchen too: coffee machines, fridges and microwaves are often overlooked but take time to deal with at the last minute. Check whether there are chemicals, batteries or other special waste that needs separate handling. The better prepared you are, the faster and cheaper the clearance will be.",
        additionalSections: [
          {
            heading: "GDPR and confidentiality during office clearance",
            content:
              "Many offices hold sensitive material — customer databases, personnel files, business contracts and old hard drives. We handle confidential material with great care. Documents can be collected in separate bags and delivered to certified document destruction. Hard drives and USB sticks are sorted out and sent to electronics recycling with traceable destruction. You receive a receipt confirming the material has been handled according to regulations, making GDPR audit documentation straightforward. We can also handle servers, NAS units and backup tapes — anything that may contain personal data or trade secrets. Security and traceability are cornerstones of our process, and we adapt to your internal policy if you have specific data-handling procedures.",
          },
          {
            heading: "Evenings, weekends and express clearance",
            content:
              "Businesses rarely have time to shut down mid-week. That's why we offer office clearance at times that suit you: early mornings, late evenings, weekends or even overnight for larger projects. Need the office empty before the weekend? We have capacity for express clearance with extra teams — often with as little as 24 hours' notice. Tell us your deadline and we'll plan around it. Many of our corporate clients appreciate that the work happens entirely outside office hours — staff leave on Friday and come back Monday morning to an empty, clean office. We're happy to coordinate with your cleaning company so the premises are move-in ready for the next tenant.",
          },
          {
            heading: "From a single room to an entire floor",
            listItems: [
              {
                label: "Single room",
                text: "A meeting room, a storeroom or an executive suite — we collect everything in a single trip.",
              },
              {
                label: "Office floor",
                text: "Open-plan layout with 20–50 workstations? We disassemble, carry out and haul away in one evening.",
              },
              {
                label: "Entire office",
                text: "Multiple floors, reception, staff canteen and server room — we project-manage the clearance from start to key handover.",
              },
              {
                label: "Co-working & pop-up",
                text: "Quick turnaround clearance of workspaces when tenants change or the space is being refitted.",
              },
            ],
            afterContent:
              "Regardless of size, you receive a fixed quote before we start, so there are no surprises on the invoice.",
          },
          {
            heading: "Why book office clearance with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book with us, you don't just get professional help — you also contribute to young people entering the workforce and building their future. Every job is staffed by motivated young workers who learn everything from customer interaction and logistics to physical labour and responsible waste management. Since our founding in 2014, we have helped hundreds of businesses clear their offices, and along the way we have given thousands of young people their first real work experience. That means every office clearance you book is more than just a service — it's an investment in the next generation. Our workers receive hands-on training in teamwork, customer communication, logistics planning and taking responsibility for heavy lifting and sorting. They learn the importance of punctuality, attention to detail and delivering a result the client is happy with. This shows in the quality of the work: our teams are engaged, energetic and eager to do their best. We have built a culture where every job is seen as an opportunity to prove oneself, and it's reflected in how carefully and efficiently the work is carried out. As a business, you also get a partner that shares your values around sustainability and social responsibility. All material we collect is sorted for maximum recycling — furniture still in good condition goes to reuse, electronics to certified e-waste handling and other waste to approved disposal. We document the handling so you can use it in your sustainability work and CSR reporting. Choosing Ungdomskraft for your office clearance means you get a fast, reliable and affordable job done — while supporting young people on their way into working life and contributing to more sustainable handling of office waste. It's professional help with purpose.",
          },
        ],
      },
      faq: [
        {
          question: "Can you clear the office in the evenings or at weekends?",
          answer:
            "Yes, we offer office clearance evenings, weekends and, when needed, overnight. Many businesses prefer us to work outside office hours to avoid disrupting operations. Just specify your preferred time when booking and we'll accommodate.",
        },
        {
          question: "How do you handle confidential material and hard drives?",
          answer:
            "Confidential documents are collected separately and delivered to certified document destruction. Hard drives, USB sticks and other storage media are sorted out and sent to electronics recycling with traceable destruction. You receive a receipt confirming the material has been handled correctly.",
        },
        {
          question: "Is furniture disassembly included in the price?",
          answer:
            "Yes, we disassemble desks, shelving systems and cabinets as part of the clearance. You don't need to prepare anything — we bring the tools and have experience with all common office furniture from IKEA, Kinnarps, AJ Products and more.",
        },
        {
          question: "What happens to the IT equipment?",
          answer:
            "Working electronics are sent for reuse and certified data wiping. Broken equipment goes to e-waste recycling. We sort everything on-site so you don't have to figure out what goes where.",
        },
        {
          question: "Can you invoice the company?",
          answer:
            "Yes, we invoice with 30-day payment terms. Enter your company registration number and billing address when booking. VAT is specified on the invoice.",
        },
        {
          question: "How quickly can you clear an entire office?",
          answer:
            "A smaller office (5–10 workstations) can usually be cleared in one evening. Larger offices with 30+ workstations typically take one to two days. We can deploy extra teams if you have a tight deadline — get in touch for a schedule.",
        },
        {
          question: "How much does office clearance cost?",
          answer:
            "The price depends on the volume of furniture, electronics and waste. A small office with 5–10 workstations often lands at SEK 3,000–6,000. Larger offices are quoted individually. You see an estimated price instantly when booking and receive a fixed quote after a walkthrough.",
        },
        {
          question: "Can you clear just part of an office?",
          answer:
            "Yes, we clear anything from a single meeting room or office to an entire floor. Need just one room emptied while the rest stays? No problem – describe the scope when booking and we adapt the team and price to your needs.",
        },
        {
          question: "Do you handle office clearance for large open-plan offices?",
          answer:
            "Absolutely. We have experience clearing open-plan offices with 50+ workstations. We deploy multiple teams in parallel and coordinate logistics with lifts, loading docks and transport routes to minimise disruption. Get in touch for a tailored schedule.",
        },
        {
          question: "How do you ensure environmentally compliant recycling of office electronics?",
          answer:
            "All electronics – computers, monitors, printers and servers – are taken to certified WEEE receivers who handle e-waste in accordance with current regulations. We document the handling so your company can demonstrate compliance with producer responsibility. Hard drives can be destroyed on site if required.",
        },
        {
          question: "Can you clear office archives and filing cabinets?",
          answer:
            "Yes, we clear filing cabinets, shelving systems and archive cupboards. Paper that does not contain sensitive information is taken to paper recycling. If you need secure document destruction, we can coordinate with a certified partner who shreds the material for you.",
        },
        {
          question: "Do you coordinate the office clearance with the building management?",
          answer:
            "Yes, we can liaise directly with your property manager or landlord regarding schedules, loading docks and lift usage. This makes the office clearance smoother and ensures the building's rules are followed.",
        },
        {
          question: "Is the office clearance insured?",
          answer:
            "Yes, all our jobs – including office clearances – are insured through Trygg-Hansa. This means your premises, your belongings and our workers are protected throughout the entire process, from disassembly to transport and removal.",
        },
        {
          question: "Which areas do you offer office clearance in?",
          answer:
            "We offer office clearance throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Office clearance in 3 easy steps",
        step2Text:
          "Our workers go through the office systematically: furniture is disassembled, electronics sorted, confidential material separated and everything is carried down to our vehicle. You decide what gets kept.",
        ctaHeading: "Book office clearance today",
        ctaText:
          "Enter the office address, describe what needs to go and pick a time. You see the price instantly — we handle the rest.",
      },
    },
  },
];
